import { useState, useRef, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link, useSearchParams } from 'react-router-dom';
import { useCart } from '../../hooks/useCart';
import { useCurrency } from '../../hooks/useCurrency';
import { useLanguage } from '../../hooks/useLanguage';
import { useFormValidation } from '../../hooks/useFormValidation';
import { formatCOP, formatUSD, formatEUR, convertToUSD, convertToEUR, getDisplayPrice, getSecondaryPrice } from '../../lib/currency';
import { supabase } from '../../lib/supabase';
import RecommendedProducts from './components/RecommendedProducts';
import ColombianPaymentsBanner from './components/ColombianPaymentsBanner';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const PROMO_CODES_KEY = 'promo-codes-5off';

/**
 * Genera un código promocional único de 5% de descuento
 */
const generatePromoCode = (): string => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = 'GRACIAS';
  for (let i = 0; i < 5; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

/**
 * Guarda un código promocional en localStorage
 */
const savePromoCode = (code: string) => {
  try {
    const existing = JSON.parse(localStorage.getItem(PROMO_CODES_KEY) || '[]');
    existing.push({ code: code.toLowerCase(), discount: 5, used: false, createdAt: Date.now() });
    localStorage.setItem(PROMO_CODES_KEY, JSON.stringify(existing));
  } catch (e) {
    console.error('Error saving promo code', e);
  }
};

/**
 * Valida si un código promocional es válido y no ha sido usado
 */
const validatePromoCode = (code: string): { valid: boolean; discount: number } => {
  try {
    const codes = JSON.parse(localStorage.getItem(PROMO_CODES_KEY) || '[]');
    const found = codes.find((c: any) => c.code === code.toLowerCase() && !c.used);
    if (found) return { valid: true, discount: found.discount };
  } catch (e) {
    console.error('Error validating promo code', e);
  }
  return { valid: false, discount: 0 };
};

/**
 * Marca un código promocional como usado
 */
const markPromoCodeUsed = (code: string) => {
  try {
    const codes = JSON.parse(localStorage.getItem(PROMO_CODES_KEY) || '[]');
    const updated = codes.map((c: any) =>
      c.code === code.toLowerCase() ? { ...c, used: true } : c
    );
    localStorage.setItem(PROMO_CODES_KEY, JSON.stringify(updated));
  } catch (e) {
    console.error('Error marking promo code', e);
  }
};

export default function CartPage() {
  const { items: cartItems, removeItem, updateQuantity, totalPrice, clearCart } = useCart();
  const { currency } = useCurrency();
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();

  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponError, setCouponError] = useState('');
  const [generatedPromoCode, setGeneratedPromoCode] = useState('');
  const [promoCopied, setPromoCopied] = useState(false);

  // IDs de productos de 5 dispositivos que tienen descuento adicional del 20%
  const fiveDeviceProductIds = [
    // Desde página Office
    'office2021ltsc',
    'office2024ltsc5',
    // Desde página Home
    'office2021ltsc5',
    'office2021macltsc',
    // IDs generados por nombre (fallback)
    'office-2024-professional-plus-ltsc-5-cuentas-licencia-de-por-vida-original',
    'office-2021-professional-plus-original-ltsc-5-cuentas-de-por-vida',
    'office-2021-professional-plus-para-mac-ltsc',
  ];

  // IDs de productos con descuento adicional del 10% en el carrito
  const tenPercentDiscountProductIds = [
    'office2021ltsc5v2',
    'stu-off2026ltsc5',
  ];

  // IDs de productos VPN con precio original de $44.850
  const vpnProductsWithOriginalPrice = [
    'adguard-vpn-1-año---5-dispositivos',
    'adguard-vpn-2-años---10-dispositivos'
  ];

  // Checkout modal state
  const [showCheckout, setShowCheckout] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'whatsapp' | 'transferencia' | 'mercadopago' | 'paypal' | 'nequi' | 'efecty' | 'daviplata' | ''>('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitAttempts, setSubmitAttempts] = useState(0);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [returnedOrderNumber, setReturnedOrderNumber] = useState('');
  const [paymentReturnStatus, setPaymentReturnStatus] = useState<'success' | 'failure' | 'pending' | ''>('');
  const [returnPromoCode, setReturnPromoCode] = useState('');
  const [returnPromoCopied, setReturnPromoCopied] = useState(false);
  const [returnLicenses, setReturnLicenses] = useState<any[]>([]);
  const [returnLicensesLoading, setReturnLicensesLoading] = useState(false);
  const [licenseCopied, setLicenseCopied] = useState<string>('');
  const [orderLicenses, setOrderLicenses] = useState<any[]>([]);
  const [orderLicensesLoading, setOrderLicensesLoading] = useState(false);
  const [showMobileSummary, setShowMobileSummary] = useState(false);
  const [accountCopied, setAccountCopied] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // Form validation
  const { errors, touched, validateEmail, validatePhone, validateName, sanitizeInput, handleBlur, handleChange, validateAll, resetValidation } = useFormValidation({
    customerName: {
      required: true,
      minLength: 2,
      custom: (value) => {
        if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]{2,}$/.test(value)) {
          return 'Solo se permiten letras y espacios';
        }
        return null;
      }
    },
    customerEmail: {
      required: true,
      custom: (value) => {
        if (!validateEmail(value)) {
          return 'Formato de email inválido';
        }
        return null;
      }
    },
    customerPhone: {
      required: true,
      minLength: 7,
      custom: (value) => {
        if (!validatePhone(value)) {
          return 'Formato de teléfono inválido (mín. 7 dígitos)';
        }
        return null;
      }
    },
    customerNotes: {
      maxLength: 500,
    }
  });

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // Detectar retorno de Mercado Pago
  useEffect(() => {
    const paymentParam = searchParams.get('payment');
    const orderParam = searchParams.get('order');
    if (paymentParam && orderParam) {
      setReturnedOrderNumber(orderParam);
      if (paymentParam === 'success') {
        setPaymentReturnStatus('success');
        clearCart();
        // Recuperar código promo de la orden
        fetchOrderPromoCode(orderParam);
        // Capturar pago de PayPal si aplica
        capturePayPalPayment(orderParam);
        // Recuperar licencias de la orden
        fetchOrderLicenses(orderParam);
      } else if (paymentParam === 'failure') {
        setPaymentReturnStatus('failure');
      } else if (paymentParam === 'pending') {
        setPaymentReturnStatus('pending');
        fetchOrderPromoCode(orderParam);
        capturePayPalPayment(orderParam);
        fetchOrderLicenses(orderParam);
      }
      // Limpiar params de la URL
      setSearchParams({}, { replace: true });
    }
  }, []);

  /**
   * Captura el pago de PayPal y dispara el envío automático de licencias
   */
  const capturePayPalPayment = async (orderNumber: string) => {
    try {
      // Buscar la orden para obtener el paypal order id
      const { data: order, error } = await supabase
        .from('orders')
        .select('id, payment_method, mercadopago_preference_id, payment_status')
        .eq('order_number', orderNumber)
        .maybeSingle();

      if (error || !order) return;

      // Solo capturar si es PayPal y aún no está pagado
      if (order.payment_method === 'paypal' && order.payment_status !== 'paid' && order.mercadopago_preference_id) {
        const res = await fetch(`${SUPABASE_URL}/functions/v1/capture-paypal-payment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            paypalOrderId: order.mercadopago_preference_id,
            orderNumber: orderNumber,
          }),
        });
        const result = await res.json();
        console.log('PayPal capture result:', result);
      }
    } catch (err) {
      console.error('Error capturing PayPal payment:', err);
    }
  };

  /**
   * Recupera el código promocional generado para una orden desde la BD
   */
  const fetchOrderPromoCode = async (orderNumber: string) => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('promo_code_generated')
        .eq('order_number', orderNumber)
        .maybeSingle();
      
      if (!error && data?.promo_code_generated) {
        setReturnPromoCode(data.promo_code_generated);
        // También guardar en localStorage para que funcione como cupón
        savePromoCode(data.promo_code_generated);
      }
    } catch (err) {
      console.error('Error fetching promo code:', err);
    }
  };

  /**
   * Calcula el precio con descuento del 20% para productos de 5 dispositivos
   */
  const getDiscountedPrice = (item: typeof cartItems[0]) => {
    if (fiveDeviceProductIds.includes(item.id)) {
      return Math.round(item.price * 0.8); // 20% de descuento
    }
    return item.price;
  };

  /**
   * Calcula el precio con descuento del 10% para productos especiales
   */
  const getTenPercentDiscountedPrice = (item: typeof cartItems[0]) => {
    if (tenPercentDiscountProductIds.includes(item.id)) {
      return Math.round(item.price * 0.9); // 10% de descuento
    }
    return item.price;
  };

  /**
   * Verifica si el producto es VPN con precio original de $44.850
   */
  const isVpnWithOriginalPrice = (itemId: string) => {
    return vpnProductsWithOriginalPrice.includes(itemId);
  };

  /**
   * Calcula el descuento para productos VPN con cálculo completo
   */
  const getVpnDiscount = (item: typeof cartItems[0]) => {
    if (isVpnWithOriginalPrice(item.id)) {
      const originalPrice = 44850;
      let months = 1;
      
      // Determinar cantidad de meses según el producto
      if (item.id === 'adguard-vpn-1-año---5-dispositivos') {
        months = 12;
      } else if (item.id === 'adguard-vpn-2-años---10-dispositivos') {
        months = 24;
      }
      
      const totalOriginalPrice = originalPrice * months;
      // El precio actual ya viene multiplicado por los meses
      const totalCurrentPrice = item.price;
      const discount = totalOriginalPrice - totalCurrentPrice;
      
      return { 
        originalPrice, 
        months,
        totalOriginalPrice,
        discount 
      };
    }
    return null;
  };

  /**
   * Obtiene el precio final de un item aplicando todos los descuentos
   */
  const getFinalItemPrice = (item: typeof cartItems[0]) => {
    if (fiveDeviceProductIds.includes(item.id) && !item.savingsDescription) {
      return getDiscountedPrice(item);
    }
    if (tenPercentDiscountProductIds.includes(item.id)) {
      return getTenPercentDiscountedPrice(item);
    }
    return item.price;
  };

  /**
   * Calcula el subtotal aplicando descuentos a productos de 5 dispositivos
   */
  const calculateSubtotal = () => {
    return cartItems.reduce((sum, item) => {
      const finalPrice = getFinalItemPrice(item);
      return sum + (finalPrice * item.quantity);
    }, 0);
  };

  /**
   * Apply a coupon code.
   * The function now validates the code and provides user feedback
   * while protecting against unexpected errors.
   */
  const applyCoupon = () => {
    try {
      const normalized = couponCode.trim().toLowerCase();
      setCouponError('');

      // Validar código fijo
      if (normalized === 'descuento10') {
        setCouponApplied(true);
        setCouponDiscount(10);
        return;
      }

      // Validar códigos promocionales generados
      const promoResult = validatePromoCode(normalized);
      if (promoResult.valid) {
        setCouponApplied(true);
        setCouponDiscount(promoResult.discount);
        return;
      }

      // Código no válido
      setCouponApplied(false);
      setCouponDiscount(0);
      setCouponError(t('cart.invalid_code'));
    } catch (error) {
      console.error('Error al aplicar el cupón:', error);
      setCouponError(t('cart.coupon_error'));
    }
  };

  const subtotal = calculateSubtotal();
  const discount = couponApplied ? Math.round(subtotal * (couponDiscount / 100)) : 0;

  /**
   * Calcula el descuento por volumen: 5% en el 3er producto en adelante.
   * Se ordenan los items por precio unitario (de mayor a menor) y se aplica
   * el 5% a partir del tercer producto.
   */
  const calculateBulkDiscount = () => {
    const totalItemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    if (totalItemCount < 3) return 0;

    // Expandir items por cantidad y ordenar por precio unitario
    const expandedItems: number[] = [];
    cartItems.forEach(item => {
      const unitPrice = getFinalItemPrice(item);
      for (let i = 0; i < item.quantity; i++) {
        expandedItems.push(unitPrice);
      }
    });

    // Ordenar de mayor a menor para que el descuento se aplique a los más baratos
    expandedItems.sort((a, b) => b - a);

    // Aplicar 5% a partir del 3er producto (índice 2 en adelante)
    let bulkDisc = 0;
    for (let i = 2; i < expandedItems.length; i++) {
      bulkDisc += Math.round(expandedItems[i] * 0.05);
    }
    return bulkDisc;
  };

  const bulkDiscount = calculateBulkDiscount();
  const totalItemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const total = subtotal - discount - bulkDiscount;

  const formatPrice = (price: number) => {
    if (currency === 'USD') {
      return formatUSD(convertToUSD(price));
    }
    if (currency === 'EUR') {
      return formatEUR(convertToEUR(price));
    }
    return formatCOP(price);
  };

  const formatSecondaryPrice = (price: number) => {
    if (currency === 'USD') {
      return formatCOP(price);
    }
    if (currency === 'EUR') {
      return formatCOP(price);
    }
    return formatUSD(convertToUSD(price));
  };

  const secondaryLabel = currency === 'USD' ? 'COP' : currency === 'EUR' ? 'COP' : 'USD';

  /**
   * Para métodos de pago colombianos, el monto SIEMPRE se paga en COP.
   * Esta función retorna el monto principal en COP y la equivalencia en la moneda del usuario.
   */
  const formatColombianPaymentAmount = (copAmount: number) => {
    const copFormatted = formatCOP(copAmount);
    if (currency === 'USD') {
      return {
        primary: copFormatted,
        primaryLabel: 'Pesos Colombianos (COP)',
        secondary: formatUSD(convertToUSD(copAmount)),
        secondaryLabel: 'USD',
        showEquivalence: true,
      };
    }
    if (currency === 'EUR') {
      return {
        primary: copFormatted,
        primaryLabel: 'Pesos Colombianos (COP)',
        secondary: formatEUR(convertToEUR(copAmount)),
        secondaryLabel: 'EUR',
        showEquivalence: true,
      };
    }
    return {
      primary: copFormatted,
      primaryLabel: 'Pesos Colombianos (COP)',
      secondary: formatUSD(convertToUSD(copAmount)),
      secondaryLabel: 'USD',
      showEquivalence: false,
    };
  };

  // Genera el resumen del pedido como texto
  const buildOrderSummary = () => {
    let summary = '✨ *¡Hola! Quiero realizar este pedido:*\n\n';
    
    // Lista de productos
    cartItems.forEach((item, index) => {
      const finalPrice = getFinalItemPrice(item);
      summary += `${index + 1}. *${item.name}*\n   ×${item.quantity} — ${formatPrice(finalPrice * item.quantity)}\n`;
    });
    
    // Total
    summary += `\n💰 *TOTAL A PAGAR: ${formatPrice(total)}*\n`;
    
    // Cupón si aplica
    if (couponApplied) {
      summary += `🎟️ Cupón: ${couponCode} (-${couponDiscount}%)\n`;
    }
    
    // Separador
    summary += `\n━━━━━━━━━━━━━━━━━━\n`;
    
    // Datos del cliente
    summary += `\n👤 *MIS DATOS:*\n`;
    summary += `📝 ${customerName}\n`;
    summary += `📧 ${customerEmail}\n`;
    summary += `📱 ${customerPhone}\n`;
    
    if (customerNotes) {
      summary += `💬 Nota: ${customerNotes}\n`;
    }
    
    // Instrucciones de pago
    summary += `\n━━━━━━━━━━━━━━━━━━\n`;
    summary += `\n💳 *¿CÓMO PAGAR?*\n`;
    summary += `\n✅ *MÉTODOS RECOMENDADOS:*\n`;
    summary += `\n🟣 *NEQUI* - Pago instantáneo\n`;
    summary += `   📱 Número: *3005461717*\n`;
    summary += `\n🟡 *Bancolombia* - Transferencia bancaria\n`;
    summary += `   🏦 Banco: *Bancolombia*\n`;
    summary += `   💳 Tipo: *Cuenta de Ahorros*\n`;
    summary += `   🔢 Número de cuenta: *363-524841-69*\n`;
    summary += `\n🏪 *EFECTY* - Consignación en efectivo\n`;
    summary += `   📍 Dirígete a un corresponsal Efecty\n`;
    summary += `   💵 Consignación por medio de Nequi\n`;
    summary += `   📱 Número: *3005461717*\n`;
    summary += `\n🔴 *DAVIPLATA* - Pago rápido\n`;
    summary += `   📱 Número: *3005461717*\n`;
    summary += `\n✅ *Otras opciones disponibles:*\n`;
    summary += `   • Tarjeta de crédito/débito\n`;
    summary += `   • PayPal\n`;
    summary += `   • Transferencia bancaria\n`;
    summary += `\n━━━━━━━━━━━━━━━━━━\n`;
    summary += `\n📸 *IMPORTANTE - ENVIAR COMPROBANTE:*\n`;
    summary += `\n1️⃣ Realiza tu pago por cualquiera de los métodos\n`;
    summary += `2️⃣ Toma una captura o foto del comprobante\n`;
    summary += `3️⃣ Envíalo por este mismo chat de WhatsApp\n`;
    summary += `4️⃣ Incluye tu nombre y número de pedido\n`;
    summary += `\n⚡ *Entrega inmediata* después de verificar tu pago\n`;
    summary += `📩 Recibirás tu licencia por email en minutos`;
    
    return summary;
  };

  /**
   * Recupera las licencias asignadas a una orden desde la BD.
   * Reintenta varias veces porque las licencias se asignan de forma asíncrona.
   */
  const fetchOrderLicenses = async (orderNumber: string, attempt = 1) => {
    setReturnLicensesLoading(true);
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('license_keys')
        .eq('order_number', orderNumber)
        .maybeSingle();
      
      if (!error && data?.license_keys && Array.isArray(data.license_keys) && data.license_keys.length > 0) {
        setReturnLicenses(data.license_keys);
        setReturnLicensesLoading(false);
      } else if (attempt < 8) {
        // Reintentar después de unos segundos (las licencias se asignan de forma asíncrona)
        const delay = attempt <= 3 ? 3000 : attempt <= 5 ? 5000 : 8000;
        setTimeout(() => fetchOrderLicenses(orderNumber, attempt + 1), delay);
      } else {
        setReturnLicensesLoading(false);
      }
    } catch (err) {
      console.error('Error fetching licenses:', err);
      if (attempt < 8) {
        setTimeout(() => fetchOrderLicenses(orderNumber, attempt + 1), 5000);
      } else {
        setReturnLicensesLoading(false);
      }
    }
  };

  /**
   * Recupera licencias para una orden por su ID (para el modal de éxito)
   */
  const fetchOrderLicensesById = async (orderNumber: string, attempt = 1) => {
    setOrderLicensesLoading(true);
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('license_keys')
        .eq('order_number', orderNumber)
        .maybeSingle();
      
      if (!error && data?.license_keys && Array.isArray(data.license_keys) && data.license_keys.length > 0) {
        setOrderLicenses(data.license_keys);
        setOrderLicensesLoading(false);
      } else if (attempt < 6) {
        setTimeout(() => fetchOrderLicensesById(orderNumber, attempt + 1), 4000);
      } else {
        setOrderLicensesLoading(false);
      }
    } catch (err) {
      console.error('Error fetching order licenses:', err);
      if (attempt < 6) {
        setTimeout(() => fetchOrderLicensesById(orderNumber, attempt + 1), 4000);
      } else {
        setOrderLicensesLoading(false);
      }
    }
  };

  /**
   * Copia una clave de licencia al portapapeles
   */
  const copyLicenseKey = (key: string) => {
    navigator.clipboard.writeText(key);
    setLicenseCopied(key);
    setTimeout(() => setLicenseCopied(''), 2000);
  };

  /**
   * Genera un mensaje de WhatsApp con las licencias
   */
  const buildWhatsAppLicenseMessage = (licenses: any[], orderNum: string) => {
    let msg = `🔑 *Mis licencias - Pedido ${orderNum}*\n\n`;
    licenses.forEach((lic, idx) => {
      msg += `${idx + 1}. *${lic.productName}*\n`;
      msg += `   Clave: ${lic.licenseKey}\n`;
      if (lic.instructions) msg += `   📋 ${lic.instructions}\n`;
      if (lic.downloadUrl) msg += `   ⬇️ ${lic.downloadUrl}\n`;
      msg += '\n';
    });
    msg += '¡Gracias! 🙌';
    return msg;
  };

  /**
   * Genera un mensaje de WhatsApp específico para transferencia bancaria
   */
  const buildTransferWhatsAppMessage = () => {
    let msg = '✨ *¡Hola! Quiero pagar por TRANSFERENCIA BANCARIA:*\n\n';
    cartItems.forEach((item, index) => {
      const finalPrice = getFinalItemPrice(item);
      msg += `${index + 1}. *${item.name}*\n   ×${item.quantity} — ${formatPrice(finalPrice * item.quantity)}\n`;
    });
    msg += `\n💰 *TOTAL A PAGAR: ${formatPrice(total)}*\n`;
    if (couponApplied) {
      msg += `🎟️ Cupón: ${couponCode} (-${couponDiscount}%)\n`;
    }
    msg += `\n━━━━━━━━━━━━━━━━━━\n`;
    msg += `\n👤 *MIS DATOS:*\n`;
    msg += `📝 ${customerName}\n`;
    msg += `📧 ${customerEmail}\n`;
    msg += `📱 ${customerPhone}\n`;
    if (customerNotes) {
      msg += `💬 Nota: ${customerNotes}\n`;
    }
    msg += `\n━━━━━━━━━━━━━━━━━━\n`;
    msg += `\n🏦 *MÉTODO DE PAGO: TRANSFERENCIA BANCOLOMBIA*\n`;
    msg += `💵 Monto a transferir: *${formatPrice(total)}*\n`;
    msg += `🏦 Banco: *Bancolombia*\n`;
    msg += `💳 Tipo: *Cuenta de Ahorros*\n`;
    msg += `🔢 Número de cuenta: *363-524841-69*\n`;
    msg += `\n📸 *Enviaré el comprobante por este chat*\n`;
    return msg;
  };

  /**
   * Copia el número de cuenta al portapapeles
   */
  const copyAccountNumber = () => {
    navigator.clipboard.writeText('363-524841-69');
    setAccountCopied(true);
    setTimeout(() => setAccountCopied(false), 2000);
  };

  /**
   * Componente reutilizable para mostrar licencias
   */
  const LicenseDisplay = ({ licenses, loading, orderNum }: { licenses: any[]; loading: boolean; orderNum: string }) => {
    if (loading) {
      return (
        <div className="bg-gradient-to-br from-sky-50 to-indigo-50 border border-sky-200 rounded-2xl p-5 mb-6">
          <div className="flex items-center justify-center gap-3">
            <i className="ri-loader-4-line animate-spin text-sky-600 text-xl"></i>
            <div>
              <p className="text-sm font-semibold text-sky-800">Preparando tus licencias...</p>
              <p className="text-xs text-sky-600 mt-1">Esto puede tomar unos segundos</p>
            </div>
          </div>
        </div>
      );
    }

    if (licenses.length === 0) return null;

    return (
      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-2xl p-5 mb-6 text-left">
        <div className="flex items-center justify-center gap-2 mb-4">
          <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
            <i className="ri-key-2-line text-emerald-600 text-lg"></i>
          </div>
          <h4 className="text-base font-bold text-emerald-800">
            {licenses.length === 1 ? 'Tu Licencia' : `Tus Licencias (${licenses.length})`}
          </h4>
        </div>

        <div className="space-y-3">
          {licenses.map((lic, idx) => (
            <div key={idx} className="bg-white rounded-xl p-4 border border-emerald-200 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{idx + 1}</span>
                <span className="text-sm font-semibold text-gray-800">{lic.productName}</span>
              </div>
              <div className="bg-gray-50 border-2 border-dashed border-emerald-300 rounded-lg p-3 flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Clave de Licencia</p>
                  <p className="text-base font-mono font-bold text-emerald-700 tracking-wider break-all select-all">
                    {lic.licenseKey}
                  </p>
                </div>
                <button
                  onClick={() => copyLicenseKey(lic.licenseKey)}
                  className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${
                    licenseCopied === lic.licenseKey
                      ? 'bg-green-100 text-green-700'
                      : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                  }`}
                >
                  {licenseCopied === lic.licenseKey ? (
                    <><i className="ri-check-line mr-1"></i>Copiado</>
                  ) : (
                    <><i className="ri-file-copy-line mr-1"></i>Copiar</>
                  )}
                </button>
              </div>
              {lic.instructions && (
                <div className="mt-2 p-2 bg-sky-50 rounded-lg">
                  <p className="text-[10px] text-sky-700">
                    <i className="ri-information-line mr-1"></i>
                    {lic.instructions}
                  </p>
                </div>
              )}
              {lic.downloadUrl && (
                <a
                  href={lic.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:text-emerald-700 cursor-pointer"
                >
                  <i className="ri-download-cloud-line"></i>
                  Descargar Software
                </a>
              )}
            </div>
          ))}
        </div>

        {/* Botón de WhatsApp para guardar licencias */}
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <a
            href={`https://wa.me/?text=${encodeURIComponent(buildWhatsAppLicenseMessage(licenses, orderNum))}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 inline-flex items-center justify-center gap-2 bg-green-500 text-white text-sm font-semibold px-4 py-2.5 rounded-xl hover:bg-green-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-whatsapp-line text-lg"></i>
            Guardar en WhatsApp
          </a>
          <button
            onClick={() => {
              const allKeys = licenses.map(l => `${l.productName}: ${l.licenseKey}`).join('\n');
              navigator.clipboard.writeText(allKeys);
              setLicenseCopied('all');
              setTimeout(() => setLicenseCopied(''), 2000);
            }}
            className={`flex-1 inline-flex items-center justify-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap ${
              licenseCopied === 'all'
                ? 'bg-green-100 text-green-700 border border-green-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
            }`}
          >
            {licenseCopied === 'all' ? (
              <><i className="ri-check-line"></i>¡Copiadas!</>
            ) : (
              <><i className="ri-file-copy-2-line"></i>Copiar Todas</>
            )}
          </button>
        </div>

        <p className="text-[10px] text-emerald-600 mt-3 text-center">
          <i className="ri-mail-line mr-1"></i>
          También recibirás tus licencias por correo electrónico. Guarda este mensaje.
        </p>
      </div>
    );
  };

  const handleCheckoutSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validar todos los campos
    const formData = {
      customerName: customerName.trim(),
      customerEmail: customerEmail.trim(),
      customerPhone: customerPhone.trim(),
      customerNotes: customerNotes.trim(),
    };

    const isValid = validateAll(formData);
    
    if (!isValid || !paymentMethod) {
      return;
    }

    // Prevenir doble envío
    if (isSubmitting) return;

    setIsSubmitting(true);
    setSubmitAttempts(prev => prev + 1);

    try {
      // Sanitizar inputs
      const sanitizedName = sanitizeInput(customerName.trim());
      const sanitizedEmail = sanitizeInput(customerEmail.trim());
      const sanitizedPhone = sanitizeInput(customerPhone.trim());
      const sanitizedNotes = sanitizeInput(customerNotes.trim());

      // Si es WhatsApp, abrir directamente sin llamar al backend
      if (paymentMethod === 'whatsapp') {
        const orderSummary = buildOrderSummary();
        const whatsappMsg = encodeURIComponent(
          `${orderSummary}\n\n👤 *Datos del cliente:*\nNombre: ${sanitizedName}\nEmail: ${sanitizedEmail}\nTeléfono: ${sanitizedPhone}${sanitizedNotes ? `\nNotas: ${sanitizedNotes}` : ''}`
        );
        window.open(`https://wa.me/79534940353?text=${whatsappMsg}`, '_blank');
        
        // Marcar cupón como usado si aplica
        if (couponApplied && couponCode.trim()) {
          markPromoCodeUsed(couponCode.trim());
        }

        // ⚠️ IMPORTANTE: Solo generar código promocional para métodos de pago instantáneos
        // Para transferencia bancaria, el admin lo proporcionará manualmente después de verificar el comprobante
        if (paymentMethod !== 'transferencia') {
          const newPromo = generatePromoCode();
          savePromoCode(newPromo);
          setGeneratedPromoCode(newPromo);
        }

        // Mostrar mensaje de éxito
        setOrderSuccess(true);
        setIsSubmitting(false);
        return;
      }

      // Si es Nequi, abrir WhatsApp con instrucciones de pago Nequi
      if (paymentMethod === 'nequi') {
        let nequiMsg = '✨ *¡Hola! Quiero pagar con NEQUI:*\n\n';
        cartItems.forEach((item, index) => {
          const finalPrice = getFinalItemPrice(item);
          nequiMsg += `${index + 1}. *${item.name}*\n   ×${item.quantity} — ${formatPrice(finalPrice * item.quantity)}\n`;
        });
        nequiMsg += `\n💰 *TOTAL A PAGAR: ${formatPrice(total)}*\n`;
        if (couponApplied) {
          nequiMsg += `🎟️ Cupón: ${couponCode} (-${couponDiscount}%)\n`;
        }
        nequiMsg += `\n━━━━━━━━━━━━━━━━━━\n`;
        nequiMsg += `\n👤 *MIS DATOS:*\n`;
        nequiMsg += `📝 ${sanitizedName}\n`;
        nequiMsg += `📧 ${sanitizedEmail}\n`;
        nequiMsg += `📱 ${sanitizedPhone}\n`;
        if (sanitizedNotes) {
          nequiMsg += `💬 Nota: ${sanitizedNotes}\n`;
        }
        nequiMsg += `\n━━━━━━━━━━━━━━━━━━\n`;
        nequiMsg += `\n🟣 *MÉTODO DE PAGO: NEQUI*\n`;
        nequiMsg += `📱 Número: *3005461717*\n`;
        nequiMsg += `\n📸 *Enviaré el comprobante por este chat*\n`;

        const whatsappMsg = encodeURIComponent(nequiMsg);
        window.open(`https://wa.me/79534940353?text=${whatsappMsg}`, '_blank');

        if (couponApplied && couponCode.trim()) {
          markPromoCodeUsed(couponCode.trim());
        }

        const newPromo = generatePromoCode();
        savePromoCode(newPromo);
        setGeneratedPromoCode(newPromo);

        setOrderSuccess(true);
        setIsSubmitting(false);
        return;
      }

      // Si es Efecty, abrir WhatsApp con instrucciones de pago Efecty
      if (paymentMethod === 'efecty') {
        let efectyMsg = '✨ *¡Hola! Quiero pagar con EFECTY:*\n\n';
        cartItems.forEach((item, index) => {
          const finalPrice = getFinalItemPrice(item);
          efectyMsg += `${index + 1}. *${item.name}*\n   ×${item.quantity} — ${formatPrice(finalPrice * item.quantity)}\n`;
        });
        efectyMsg += `\n💰 *TOTAL A PAGAR: ${formatPrice(total)}*\n`;
        if (couponApplied) {
          efectyMsg += `🎟️ Cupón: ${couponCode} (-${couponDiscount}%)\n`;
        }
        efectyMsg += `\n━━━━━━━━━━━━━━━━━━\n`;
        efectyMsg += `\n👤 *MIS DATOS:*\n`;
        efectyMsg += `📝 ${sanitizedName}\n`;
        efectyMsg += `📧 ${sanitizedEmail}\n`;
        efectyMsg += `📱 ${sanitizedPhone}\n`;
        if (sanitizedNotes) {
          efectyMsg += `💬 Nota: ${sanitizedNotes}\n`;
        }
        efectyMsg += `\n━━━━━━━━━━━━━━━━━━\n`;
        efectyMsg += `\n🏪 *MÉTODO DE PAGO: EFECTY*\n`;
        efectyMsg += `📍 Consignación por medio de Nequi\n`;
        efectyMsg += `📱 Número: *3005461717*\n`;
        efectyMsg += `\n📸 *Enviaré el comprobante por este chat*\n`;

        const whatsappMsg = encodeURIComponent(efectyMsg);
        window.open(`https://wa.me/79534940353?text=${whatsappMsg}`, '_blank');

        if (couponApplied && couponCode.trim()) {
          markPromoCodeUsed(couponCode.trim());
        }

        const newPromo = generatePromoCode();
        savePromoCode(newPromo);
        setGeneratedPromoCode(newPromo);

        setOrderSuccess(true);
        setIsSubmitting(false);
        return;
      }

      // Si es Daviplata, abrir WhatsApp con instrucciones de pago Daviplata
      if (paymentMethod === 'daviplata') {
        let daviplataMsg = '✨ *¡Hola! Quiero pagar con DAVIPLATA:*\n\n';
        cartItems.forEach((item, index) => {
          const finalPrice = getFinalItemPrice(item);
          daviplataMsg += `${index + 1}. *${item.name}*\n   ×${item.quantity} — ${formatPrice(finalPrice * item.quantity)}\n`;
        });
        daviplataMsg += `\n💰 *TOTAL A PAGAR: ${formatPrice(total)}*\n`;
        if (couponApplied) {
          daviplataMsg += `🎟️ Cupón: ${couponCode} (-${couponDiscount}%)\n`;
        }
        daviplataMsg += `\n━━━━━━━━━━━━━━━━━━\n`;
        daviplataMsg += `\n👤 *MIS DATOS:*\n`;
        daviplataMsg += `📝 ${sanitizedName}\n`;
        daviplataMsg += `📧 ${sanitizedEmail}\n`;
        daviplataMsg += `📱 ${sanitizedPhone}\n`;
        if (sanitizedNotes) {
          daviplataMsg += `💬 Nota: ${sanitizedNotes}\n`;
        }
        daviplataMsg += `\n━━━━━━━━━━━━━━━━━━\n`;
        daviplataMsg += `\n🔴 *MÉTODO DE PAGO: DAVIPLATA*\n`;
        daviplataMsg += `📱 Número: *3005461717*\n`;
        daviplataMsg += `\n📸 *Enviaré el comprobante por este chat*\n`;

        const whatsappMsg = encodeURIComponent(daviplataMsg);
        window.open(`https://wa.me/79534940353?text=${whatsappMsg}`, '_blank');

        if (couponApplied && couponCode.trim()) {
          markPromoCodeUsed(couponCode.trim());
        }

        const newPromo = generatePromoCode();
        savePromoCode(newPromo);
        setGeneratedPromoCode(newPromo);

        setOrderSuccess(true);
        setIsSubmitting(false);
        return;
      }

      // Preparar items para el backend (para otros métodos de pago)
      const orderItems = cartItems.map(item => {
        const finalPrice = getFinalItemPrice(item);
        return {
          id: item.id,
          name: item.name,
          type: item.type,
          quantity: item.quantity,
          unitPrice: item.price,
          finalPrice: finalPrice,
          image: item.image,
        };
      });

      // Generar número de orden único
      const orderNumber = `ORD-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;

      const payload = {
        orderNumber,
        customerName: sanitizedName,
        customerEmail: sanitizedEmail,
        customerPhone: sanitizedPhone,
        items: orderItems,
        subtotal,
        discount: discount + bulkDiscount,
        total,
        couponCode: couponApplied ? couponCode : null,
        notes: sanitizedNotes || null,
        paymentMethod,
      };

      // Si es PayPal, usar el endpoint de PayPal
      if (paymentMethod === 'paypal') {
        const response = await fetch(`${SUPABASE_URL}/functions/v1/create-paypal-order`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderData: payload }),
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Error al crear la orden de PayPal');
        }

        // Redirigir al checkout de PayPal
        if (data.approvalUrl) {
          window.location.href = data.approvalUrl;
          return;
        }
      }

      // Para otros métodos de pago, usar el endpoint de Mercado Pago
      const response = await fetch(`${SUPABASE_URL}/functions/v1/create-mercadopago-preference`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Error al procesar el pedido');
      }

      // Si es Mercado Pago, redirigir al checkout de MP
      if (paymentMethod === 'mercadopago' && data.init_point) {
        window.open(data.init_point, '_self');
        return;
      }

      setReturnedOrderNumber(data.orderNumber || '');

      // Marcar cupón como usado si aplica
      if (couponApplied && couponCode.trim()) {
        markPromoCodeUsed(couponCode.trim());
      }

      // ⚠️ IMPORTANTE: Solo generar código promocional para métodos de pago instantáneos
      // Para transferencia bancaria, el admin lo proporcionará manualmente después de verificar el comprobante
      if (paymentMethod !== 'transferencia') {
        const newPromo = generatePromoCode();
        savePromoCode(newPromo);
        setGeneratedPromoCode(newPromo);
      }

      setOrderSuccess(true);

      // Intentar recuperar licencias para esta orden
      if (data.orderNumber) {
        fetchOrderLicensesById(data.orderNumber);
      }

      // También enviar al formulario de Readdy
      try {
        const productsList = cartItems.map(item => {
          const finalPrice = getFinalItemPrice(item);
          return `${item.name} ×${item.quantity} — ${formatPrice(finalPrice * item.quantity)}`;
        }).join(' | ');

        const paymentMethodLabels: Record<string, string> = {
          whatsapp: 'WhatsApp',
          mercadopago: 'Mercado Pago',
          transferencia: 'Transferencia Bancaria',
          paypal: 'PayPal',
          nequi: 'Nequi',
          efecty: 'Efecty',
          daviplata: 'Daviplata',
        };

        const formData = new URLSearchParams();
        formData.append('nombre', sanitizedName);
        formData.append('email', sanitizedEmail);
        formData.append('telefono', sanitizedPhone);
        formData.append('metodo_pago', paymentMethodLabels[paymentMethod] || paymentMethod);
        formData.append('productos', productsList);
        formData.append('total', formatPrice(total));
        formData.append('numero_pedido', data.orderNumber || '');
        if (sanitizedNotes) formData.append('notas', sanitizedNotes);
        if (couponApplied) formData.append('cupon', `${couponCode} (${couponDiscount}%)`);

        await fetch('https://readdy.ai/api/form/d6a47ttbiueuoqm1s7lg', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: formData.toString(),
        });
      } catch (formErr) {
        console.error('Form backup error:', formErr);
      }

    } catch (err: any) {
      console.error('Error al enviar pedido:', err);
      alert(err.message || 'Error al procesar tu pedido. Intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Pantalla de retorno de Mercado Pago
  if (paymentReturnStatus) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-1 flex items-center justify-center py-8 sm:py-20 px-4">
          <div className="bg-white rounded-2xl shadow-lg p-5 sm:p-10 max-w-lg w-full text-center">
            {paymentReturnStatus === 'success' && (
              <>
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                  <i className="ri-check-double-line text-3xl sm:text-4xl text-green-600"></i>
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">{t('cart.payment_success')}</h2>
                <p className="text-sm sm:text-base text-gray-600 mb-2">{t('cart.payment_processed')}</p>
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                  <p className="text-xs sm:text-sm text-green-800 font-medium">
                    <i className="ri-file-list-3-line mr-1"></i>
                    {t('cart.order_label')}: <strong className="break-all">{returnedOrderNumber}</strong>
                  </p>
                  <p className="text-[10px] sm:text-xs text-green-700 mt-2">
                    {t('cart.license_by_email')}
                  </p>
                </div>

                {/* Licencias del pedido */}
                <LicenseDisplay licenses={returnLicenses} loading={returnLicensesLoading} orderNum={returnedOrderNumber} />

                {/* Código Promocional de Regalo */}
                {returnPromoCode && (
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-dashed border-amber-300 rounded-2xl p-4 sm:p-5 mb-4 sm:mb-6 relative overflow-hidden text-left">
                    <div className="absolute top-0 right-0 w-16 h-16 sm:w-20 sm:h-20 bg-amber-200/30 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                    <div className="absolute bottom-0 left-0 w-12 h-12 sm:w-16 sm:h-16 bg-orange-200/30 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                    <div className="relative">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <i className="ri-gift-2-line text-amber-600 text-xl"></i>
                        <h4 className="text-sm sm:text-base font-bold text-amber-800">{t('cart.thanks_purchase')}</h4>
                      </div>
                      <p className="text-[10px] sm:text-xs text-amber-700 mb-2 sm:mb-3">
                        {t('cart.promo_desc')}
                      </p>
                      <div className="bg-white rounded-xl p-3 sm:p-4 border border-amber-200">
                        <p className="text-[10px] sm:text-xs text-amber-800 font-medium mb-1 sm:mb-2">
                          <i className="ri-file-list-3-line mr-1"></i>
                          {t('cart.promo_code_label')}: <strong>{returnPromoCode}</strong>
                        </p>
                        <p className="text-[10px] sm:text-xs text-amber-700 mt-1">
                          {t('cart.promo_email_note')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
            {paymentReturnStatus === 'pending' && (
              <>
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                  <i className="ri-time-line text-3xl sm:text-4xl text-amber-600"></i>
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">{t('cart.payment_pending')}</h2>
                <p className="text-sm sm:text-base text-gray-600 mb-2">{t('cart.payment_processing')}</p>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                  <p className="text-xs sm:text-sm text-amber-800 font-medium">
                    <i className="ri-file-list-3-line mr-2"></i>
                    {t('cart.order_label')}: <strong className="break-all">{returnedOrderNumber}</strong>
                  </p>
                  <p className="text-[10px] sm:text-xs text-amber-700 mt-2">
                    {t('cart.notify_email')}
                  </p>
                </div>

                {/* Licencias del pedido */}
                <LicenseDisplay licenses={returnLicenses} loading={returnLicensesLoading} orderNum={returnedOrderNumber} />

                {/* Código Promocional de Regalo */}
                {returnPromoCode && (
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-dashed border-amber-300 rounded-2xl p-4 sm:p-5 mb-4 sm:mb-6 relative overflow-hidden text-left">
                    <div className="absolute top-0 right-0 w-16 h-16 sm:w-20 sm:h-20 bg-amber-200/30 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                    <div className="absolute bottom-0 left-0 w-12 h-12 sm:w-16 sm:h-16 bg-orange-200/30 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                    <div className="relative">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <i className="ri-gift-2-line text-amber-600 text-xl"></i>
                        <h4 className="text-sm sm:text-base font-bold text-amber-800">{t('cart.thanks_purchase')}</h4>
                      </div>
                      <p className="text-[10px] sm:text-xs text-amber-700 mb-2 sm:mb-3">
                        {t('cart.promo_desc')}
                      </p>
                      <div className="bg-white rounded-xl p-3 sm:p-4 border border-amber-200">
                        <p className="text-[10px] sm:text-xs text-amber-800 font-medium mb-1 sm:mb-2">
                          <i className="ri-file-list-3-line mr-1"></i>
                          {t('cart.promo_code_label')}: <strong>{returnPromoCode}</strong>
                        </p>
                        <p className="text-[10px] sm:text-xs text-amber-700 mt-1">
                          {t('cart.promo_email_note')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
            {paymentReturnStatus === 'failure' && (
              <>
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                  <i className="ri-close-circle-line text-3xl sm:text-4xl text-red-600"></i>
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">{t('cart.payment_failed')}</h2>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                  {t('cart.payment_failed_desc')}
                </p>
              </>
            )}
            <Link
              to="/"
              className="inline-block w-full bg-gradient-to-r from-[#0078D4] to-[#00a2ed] text-white py-3 rounded-xl font-semibold text-sm sm:text-base hover:from-[#006abc] hover:to-[#0090d4] transition-all cursor-pointer whitespace-nowrap"
            >
              {t('cart.back_to_store')}
            </Link>
            {paymentReturnStatus === 'failure' && (
              <button
                onClick={() => setPaymentReturnStatus('')}
                className="mt-3 w-full py-3 rounded-xl font-semibold text-sm sm:text-base border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
              >
                {t('cart.try_again')}
              </button>
            )}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const handleCloseSuccess = () => {
    setOrderSuccess(false);
    setShowCheckout(false);
    setPaymentMethod('');
    setCustomerName('');
    setCustomerEmail('');
    setCustomerPhone('');
    setCustomerNotes('');
    setGeneratedPromoCode('');
    setPromoCopied(false);
    setOrderLicenses([]);
    setOrderLicensesLoading(false);
    clearCart();
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      {/* Banner Superior */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200 py-2 px-3 sm:px-4">
        <div className="container mx-auto flex flex-col items-center justify-center gap-1 sm:gap-2 text-center">
          <p className="text-[10px] sm:text-sm text-amber-800 font-medium flex items-center gap-1 sm:gap-2">
            <i className="ri-flashlight-line text-xs sm:text-base"></i>
            <span className="leading-tight">{t('cart.immediate_access')}</span>
          </p>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <span className="text-[9px] sm:text-xs text-amber-700">{t('cart.contact_us')}:</span>
            <a
              href="https://wa.me/79534940353"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-0.5 sm:gap-1 bg-green-500 text-white text-[9px] sm:text-xs font-semibold px-1.5 py-0.5 sm:px-3 sm:py-1.5 rounded-full hover:bg-green-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line"></i>
              WhatsApp
            </a>
            <a
              href="https://t.me/Sup0rteWin"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-0.5 sm:gap-1 bg-sky-500 text-white text-[9px] sm:text-xs font-semibold px-1.5 py-0.5 sm:px-3 sm:py-1.5 rounded-full hover:bg-sky-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-telegram-line"></i>
              Telegram
            </a>
          </div>
        </div>
      </div>

      {/* Banner descuento por volumen */}
      {totalItemCount < 3 && cartItems.length > 0 && (
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-emerald-200 py-1.5 sm:py-3 px-3 sm:px-4 text-center">
          <p className="text-[10px] sm:text-sm text-emerald-800 font-medium">
            <i className="ri-gift-line mr-1"></i>
            {t('cart.volume_add')} {3 - totalItemCount} {t('cart.volume_more')} <strong>5% OFF</strong> {t('cart.volume_from')}
          </p>
        </div>
      )}
      {totalItemCount >= 3 && (
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-emerald-200 py-1.5 sm:py-3 px-3 sm:px-4 text-center">
          <p className="text-[10px] sm:text-sm text-emerald-800 font-medium">
            <i className="ri-discount-percent-line mr-1"></i>
            {t('cart.volume_applied')} <strong>5% OFF</strong> — {t('cart.you_save')} <strong>{formatPrice(bulkDiscount)}</strong>
          </p>
        </div>
      )}

      {/* Banner Métodos de Pago Colombianos */}
      {cartItems.length > 0 && <ColombianPaymentsBanner />}

      <main className="flex-1 py-4 sm:py-12 pb-24 lg:pb-12">
        <div className="container mx-auto px-3 sm:px-4">
          {/* Barra de Progreso */}
          <div className="flex items-center justify-center mb-5 sm:mb-12">
            <div className="flex items-center">
              <div className="flex flex-col items-center">
                <div className="w-7 h-7 sm:w-10 sm:h-10 bg-[#0078D4] rounded-full flex items-center justify-center text-white font-semibold text-xs sm:text-base">
                  1
                </div>
                <span className="text-[10px] sm:text-sm font-medium text-gray-900 mt-1 sm:mt-2">{t('cart.cart')}</span>
              </div>
              <div className="w-8 sm:w-24 h-0.5 sm:h-1 bg-gray-300 mx-1 sm:mx-2"></div>
              <div className="flex flex-col items-center">
                <div className="w-7 h-7 sm:w-10 sm:h-10 bg-gray-300 rounded-full flex items-center justify-center text-gray-600 font-semibold text-xs sm:text-base">
                  2
                </div>
                <span className="text-[10px] sm:text-sm text-gray-500 mt-1 sm:mt-2">{t('cart.info')}</span>
              </div>
              <div className="w-8 sm:w-24 h-0.5 sm:h-1 bg-gray-300 mx-1 sm:mx-2"></div>
              <div className="flex flex-col items-center">
                <div className="w-7 h-7 sm:w-10 sm:h-10 bg-gray-300 rounded-full flex items-center justify-center text-gray-600 font-semibold text-xs sm:text-base">
                  3
                </div>
                <span className="text-[10px] sm:text-sm font-medium text-gray-500 mt-1 sm:mt-2">{t('cart.payment')}</span>
              </div>
            </div>
          </div>

          {/* Título */}
          <div className="mb-4 sm:mb-8">
            <h1 className="text-xl sm:text-4xl font-bold text-gray-900">{t('cart.your_cart')}</h1>
            <p className="text-xs sm:text-base text-gray-600 mt-0.5 sm:mt-2">
              {cartItems.length}{' '}
              {cartItems.length === 1 ? t('cart.product_in_cart') : t('cart.products_in_cart')}
            </p>
          </div>

          {cartItems.length === 0 ? (
            /* Carrito Vacío */
            <div className="bg-white rounded-xl p-6 sm:p-12 text-center shadow-sm">
              <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-6">
                <i className="ri-shopping-cart-line text-2xl sm:text-4xl text-gray-400"></i>
              </div>
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-4">
                {t('cart.empty_cart')}
              </h2>
              <p className="text-xs sm:text-base text-gray-600 mb-4 sm:mb-8">
                {t('cart.explore_desc')}
              </p>
              <Link
                to="/"
                className="inline-block bg-[#0078D4] text-white px-5 py-2.5 sm:px-8 sm:py-3 rounded-lg font-semibold hover:bg-[#006abc] transition-colors cursor-pointer whitespace-nowrap text-sm sm:text-base"
              >
                {t('cart.explore_products')}
              </Link>
            </div>
          ) : (
            /* Contenido del Carrito */
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
              {/* Columna Izquierda - Productos */}
              <div className="lg:col-span-2 space-y-3">
                {cartItems.map((item, index) => {
                  const hasDiscount = fiveDeviceProductIds.includes(item.id);
                  const discountedPrice = getDiscountedPrice(item);
                  const hasTenPercentDiscount = tenPercentDiscountProductIds.includes(item.id);
                  const tenPercentPrice = getTenPercentDiscountedPrice(item);
                  const vpnDiscount = getVpnDiscount(item);
                  
                  return (
                    <div
                      key={`cart-item-${item.id}-${index}`}
                      className="bg-white rounded-xl p-3 sm:p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                    >
                      <div className="flex gap-2.5 sm:gap-6">
                        {/* Imagen */}
                        <div className="w-14 h-14 sm:w-24 sm:h-24 bg-gray-50 rounded-lg flex-shrink-0 flex items-center justify-center p-1 sm:p-2">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-contain"
                          />
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start gap-1.5 sm:gap-2">
                            <div className="min-w-0">
                              <h3 className="font-semibold text-gray-900 text-xs sm:text-lg leading-tight line-clamp-2">
                                {item.name}
                              </h3>
                              <p className="text-[9px] sm:text-sm text-gray-500 mt-0.5">{item.type}</p>
                              <div className="flex items-center gap-1 sm:gap-2 mt-1 sm:mt-2 flex-wrap">
                                <span className="inline-block px-1.5 py-0.5 sm:px-3 sm:py-1 bg-blue-50 text-blue-700 text-[10px] sm:text-xs font-medium rounded-full">
                                  <i className="ri-download-cloud-line mr-0.5"></i>
                                  {t('cart.digital')}
                                </span>
                                {hasDiscount && (
                                  <span className="inline-block px-1.5 py-0.5 sm:px-3 sm:py-1 bg-green-50 text-green-700 text-[10px] sm:text-xs font-medium rounded-full">
                                    <i className="ri-discount-percent-line mr-0.5"></i>
                                    {t('cart.off_20')}
                                  </span>
                                )}
                                {hasTenPercentDiscount && (
                                  <span className="inline-block px-1.5 py-0.5 sm:px-3 sm:py-1 bg-orange-50 text-orange-700 text-[10px] sm:text-xs font-medium rounded-full">
                                    <i className="ri-fire-line mr-1"></i>
                                    -10% Edición Especial
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => removeItem(item.id)}
                              className="text-gray-400 hover:text-red-500 transition-colors cursor-pointer p-0.5 sm:p-2 flex-shrink-0"
                            >
                              <i className="ri-delete-bin-line text-base sm:text-xl"></i>
                            </button>
                          </div>

                          {item.savingsDescription && (
                            <div className="mt-1.5 sm:mt-2 p-1.5 sm:p-3 bg-amber-50 border border-amber-200 rounded-lg">
                              <p className="text-[10px] sm:text-xs text-amber-800 font-medium">
                                <i className="ri-price-tag-3-line mr-1"></i>
                                {item.savingsDescription}
                              </p>
                            </div>
                          )}
                          {hasTenPercentDiscount && (
                            <div className="mt-1.5 sm:mt-2 p-1.5 sm:p-3 bg-orange-50 border border-orange-200 rounded-lg">
                              <p className="text-[10px] sm:text-xs text-orange-800 font-medium">
                                <i className="ri-fire-line mr-1"></i>
                                Descuento exclusivo del 10% aplicado — Ahorras {formatPrice(Math.round(item.price * 0.1) * item.quantity)}
                              </p>
                            </div>
                          )}
                          {vpnDiscount && (
                            <div className="mt-1.5 sm:mt-2 p-1.5 sm:p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                              <p className="text-[10px] sm:text-xs text-emerald-700 font-medium">
                                <i className="ri-price-tag-3-line mr-1"></i>
                                Original: {formatPrice(vpnDiscount.originalPrice)} × {vpnDiscount.months} meses = {formatPrice(vpnDiscount.totalOriginalPrice)}
                              </p>
                              <p className="text-[10px] sm:text-xs text-emerald-700 font-bold">
                                Ahorras {formatPrice(vpnDiscount.discount)}
                              </p>
                            </div>
                          )}

                          <div className="flex items-center justify-between mt-2 sm:mt-4 gap-2">
                            {/* Selector de Cantidad */}
                            <div className="flex items-center border border-gray-200 rounded-lg">
                              <button
                                onClick={() =>
                                  updateQuantity(item.id, Math.max(item.quantity - 1, 1))
                                }
                                className="w-7 h-7 sm:w-10 sm:h-10 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer rounded-l-lg"
                              >
                                <i className="ri-subtract-line text-xs sm:text-base"></i>
                              </button>
                              <span className="w-7 h-7 sm:w-12 sm:h-10 flex items-center justify-center bg-gray-50 font-medium text-xs sm:text-base">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                className="w-7 h-7 sm:w-10 sm:h-10 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer rounded-r-lg"
                              >
                                <i className="ri-add-line text-xs sm:text-base"></i>
                              </button>
                            </div>

                            {/* Precio */}
                            <div className="text-right">
                              {item.oldPrice && item.oldPrice > 0 && (
                                <span className="text-[9px] sm:text-sm text-gray-400 line-through block">
                                  {formatPrice(item.oldPrice * item.quantity)}
                                </span>
                              )}
                              {hasDiscount && !item.savingsDescription && (
                                <span className="text-[9px] sm:text-sm text-gray-400 line-through block">
                                  {formatPrice(item.price * item.quantity)}
                                </span>
                              )}
                              {hasTenPercentDiscount && (
                                <span className="text-[9px] sm:text-sm text-gray-400 line-through block">
                                  {formatPrice(item.price * item.quantity)}
                                </span>
                              )}
                              <span className="text-sm sm:text-xl font-bold text-gray-900">
                                {hasDiscount && !item.savingsDescription
                                  ? formatPrice(discountedPrice * item.quantity)
                                  : hasTenPercentDiscount
                                    ? formatPrice(tenPercentPrice * item.quantity)
                                    : formatPrice(item.price * item.quantity)
                                }
                              </span>
                              <p className="text-[9px] sm:text-xs text-emerald-600 font-medium mt-0.5">
                                ≈ {hasDiscount && !item.savingsDescription
                                  ? formatSecondaryPrice(discountedPrice * item.quantity)
                                  : hasTenPercentDiscount
                                    ? formatSecondaryPrice(tenPercentPrice * item.quantity)
                                    : formatSecondaryPrice(item.price * item.quantity)
                                } {secondaryLabel}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Código de Descuento */}
                <div className="bg-white rounded-xl p-3 sm:p-6 shadow-sm border border-gray-100">
                  <h3 className="font-semibold text-gray-900 mb-2 sm:mb-4 text-xs sm:text-base">
                    <i className="ri-coupon-3-line mr-1.5 sm:mr-2 text-[#0078D4]"></i>
                    {t('cart.discount_code')}
                  </h3>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={e => setCouponCode(e.target.value)}
                      placeholder={t('cart.enter_code')}
                      className="flex-1 px-3 py-2 sm:px-4 sm:py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0078D4] text-xs sm:text-sm min-w-0"
                      disabled={couponApplied}
                    />
                    <button
                      onClick={applyCoupon}
                      disabled={couponApplied || !couponCode.trim()}
                      className={`px-3 py-2 sm:px-6 sm:py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap text-xs sm:text-sm ${
                        couponApplied
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-900 text-white hover:bg-gray-800'
                      }`}
                    >
                      {couponApplied ? (
                        <>
                          <i className="ri-check-line mr-0.5 sm:mr-1"></i>
                          OK
                        </>
                      ) : (
                        t('cart.apply')
                      )}
                    </button>
                  </div>
                  {couponApplied && (
                    <p className="text-green-600 text-[10px] sm:text-sm mt-1.5 sm:mt-2">
                      <i className="ri-check-double-line mr-1"></i>
                      {t('cart.code_applied')} {couponDiscount}% {t('cart.discount_of')}
                    </p>
                  )}
                  {couponError && !couponApplied && (
                    <p className="text-red-500 text-[10px] sm:text-sm mt-1.5 sm:mt-2">
                      <i className="ri-error-warning-line mr-1"></i>
                      {couponError}
                    </p>
                  )}
                </div>

                {/* Continuar Comprando */}
                <Link
                  to="/"
                  className="inline-flex items-center text-[#0078D4] hover:text-[#006abc] font-medium cursor-pointer text-xs sm:text-sm"
                >
                  <i className="ri-arrow-left-line mr-1.5 sm:mr-2"></i>
                  {t('cart.continue_shopping')}
                </Link>
              </div>

              {/* Columna Derecha - Resumen */}
              <div className="lg:col-span-1">
                {/* Mobile: Always visible summary */}
                <div className="lg:hidden">
                  <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <i className="ri-file-list-3-line text-[#0078D4]"></i>
                        <span className="text-sm font-semibold text-gray-900">{t('cart.order_summary')}</span>
                      </div>
                      <span className="text-sm font-bold text-gray-900">{formatPrice(total)}</span>
                    </div>
                  
                    <div>
                      {/* Desglose de productos */}
                      <div className="space-y-2 pb-3 border-b border-gray-100 mb-3">
                        {cartItems.map((item, index) => {
                          const finalPrice = getFinalItemPrice(item);
                          return (
                            <div key={`msummary-item-${item.id}-${index}`} className="flex justify-between items-start gap-2">
                              <span className="text-[10px] text-gray-600 leading-tight flex-1 line-clamp-1">{item.name} <span className="text-gray-400">×{item.quantity}</span></span>
                              <span className="text-[10px] font-medium text-gray-900 whitespace-nowrap flex-shrink-0">
                                {formatPrice(finalPrice * item.quantity)}
                              </span>
                            </div>
                          );
                        })}
                      </div>

                      {/* Discounts summary mobile */}
                      <div className="space-y-1.5 text-[10px]">
                        {(() => {
                          const savingsFiveDevices = cartItems.reduce((sum, item) => {
                            if (fiveDeviceProductIds.includes(item.id) && !item.savingsDescription) {
                              return sum + Math.round(item.price * 0.2) * item.quantity;
                            }
                            return sum;
                          }, 0);
                          const savingsOriginalPrice = cartItems.reduce((sum, item) => {
                            if (item.oldPrice && item.oldPrice > 0) {
                              return sum + (item.oldPrice - item.price) * item.quantity;
                            }
                            return sum;
                          }, 0);
                          const savingsBulk = cartItems.reduce((sum, item) => {
                            if (item.savingsDescription && fiveDeviceProductIds.includes(item.id)) {
                              if (item.oldPrice && item.oldPrice > 0) {
                                return sum + (item.oldPrice - item.price) * item.quantity;
                              }
                            }
                            return sum;
                          }, 0);
                          const savingsVpn = cartItems.reduce((sum, item) => {
                            const vpnInfo = getVpnDiscount(item);
                            if (vpnInfo) return sum + vpnInfo.discount * item.quantity;
                            return sum;
                          }, 0);
                          const totalSavings = savingsFiveDevices + savingsOriginalPrice + savingsBulk + savingsVpn + discount + bulkDiscount;

                          return (
                            <>
                              <div className="flex justify-between text-gray-600">
                                <span>{t('cart.subtotal')}</span>
                                <span>{formatPrice(subtotal)}</span>
                              </div>
                              {couponApplied && (
                                <div className="flex justify-between text-green-600">
                                  <span>{t('cart.discount_coupon')} ({couponDiscount}%)</span>
                                  <span>-{formatPrice(discount)}</span>
                                </div>
                              )}
                              {bulkDiscount > 0 && (
                                <div className="flex justify-between text-emerald-600">
                                  <span>{t('cart.discount_volume')}</span>
                                  <span>-{formatPrice(bulkDiscount)}</span>
                                </div>
                              )}
                              <div className="flex justify-between text-gray-600">
                                <span>{t('cart.shipping')}</span>
                                <span className="text-green-600 font-medium">{t('cart.free')}</span>
                              </div>
                              {totalSavings > 0 && (
                                <div className="flex justify-between text-green-700 font-semibold bg-green-50 rounded-lg p-2 mt-1">
                                  <span><i className="ri-trophy-line mr-1"></i>{t('cart.total_saved')}</span>
                                  <span>-{formatPrice(totalSavings)}</span>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Desktop: Full summary */}
                <div className="hidden lg:block bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100 lg:sticky lg:top-32">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">
                    {t('cart.order_summary')}
                  </h3>

                  {/* Desglose de productos */}
                  <div className="space-y-3 pb-4 border-b border-gray-100 mb-4">
                    {cartItems.map((item, index) => {
                      const finalPrice = getFinalItemPrice(item);
                      return (
                        <div key={`summary-item-${item.id}-${index}`} className="flex justify-between items-start gap-2">
                          <span className="text-sm text-gray-600 leading-tight flex-1">{item.name} <span className="text-gray-400">×{item.quantity}</span></span>
                          <div className="text-right flex-shrink-0">
                            <span className="text-sm font-medium text-gray-900 whitespace-nowrap block">
                              {formatPrice(finalPrice * item.quantity)}
                            </span>
                            <span className="text-[10px] text-emerald-600 font-medium">≈ {formatSecondaryPrice(finalPrice * item.quantity)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="space-y-4 pb-6 border-b border-gray-100">
                    <div className="flex justify-between text-gray-600">
                      <span>{t('cart.subtotal')}</span>
                      <div className="text-right">
                        <span className="block">{formatPrice(subtotal)}</span>
                        <span className="text-[10px] text-emerald-600">≈ {formatSecondaryPrice(subtotal)}</span>
                      </div>
                    </div>

                    {/* Descuentos desglosados */}
                    {(() => {
                      const savingsFiveDevices = cartItems.reduce((sum, item) => {
                        if (fiveDeviceProductIds.includes(item.id) && !item.savingsDescription) {
                          return sum + Math.round(item.price * 0.2) * item.quantity;
                        }
                        return sum;
                      }, 0);
                      const savingsOriginalPrice = cartItems.reduce((sum, item) => {
                        if (item.oldPrice && item.oldPrice > 0) {
                          return sum + (item.oldPrice - item.price) * item.quantity;
                        }
                        return sum;
                      }, 0);
                      const savingsBulk = cartItems.reduce((sum, item) => {
                        if (item.savingsDescription && fiveDeviceProductIds.includes(item.id)) {
                          if (item.oldPrice && item.oldPrice > 0) {
                            return sum + (item.oldPrice - item.price) * item.quantity;
                          }
                        }
                        return sum;
                      }, 0);
                      const savingsVpn = cartItems.reduce((sum, item) => {
                        const vpnInfo = getVpnDiscount(item);
                        if (vpnInfo) {
                          return sum + vpnInfo.discount * item.quantity;
                        }
                        return sum;
                      }, 0);
                      const totalSavings = savingsFiveDevices + savingsOriginalPrice + savingsBulk + savingsVpn + discount + bulkDiscount;

                      return (
                        <>
                          {savingsFiveDevices > 0 && (
                            <div className="flex justify-between text-green-600 text-sm">
                              <span className="flex items-center gap-1">
                                <i className="ri-discount-percent-line"></i>
                                {t('cart.discount_5devices')}
                              </span>
                              <span>-{formatPrice(savingsFiveDevices)}</span>
                            </div>
                          )}
                          {(savingsOriginalPrice > 0 || savingsBulk > 0) && (
                            <div className="flex justify-between text-green-600 text-sm">
                              <span className="flex items-center gap-1">
                                <i className="ri-price-tag-3-line"></i>
                                {t('cart.discount_original')}
                              </span>
                              <span>-{formatPrice(savingsOriginalPrice + savingsBulk)}</span>
                            </div>
                          )}
                          {savingsVpn > 0 && (
                            <div className="flex justify-between text-green-600 text-sm">
                              <span className="flex items-center gap-1">
                                <i className="ri-shield-line"></i>
                                {t('cart.discount_vpn')}
                              </span>
                              <span>-{formatPrice(savingsVpn)}</span>
                            </div>
                          )}
                          {couponApplied && (
                            <div className="flex justify-between text-green-600 text-sm">
                              <span className="flex items-center gap-1">
                                <i className="ri-coupon-3-line"></i>
                                {t('cart.discount_coupon')} ({couponDiscount}%)
                              </span>
                              <span>-{formatPrice(discount)}</span>
                            </div>
                          )}
                          {bulkDiscount > 0 && (
                            <div className="flex justify-between text-emerald-600 text-sm">
                              <span className="flex items-center gap-1">
                                <i className="ri-gift-line"></i>
                                {t('cart.discount_volume')}
                              </span>
                              <span>-{formatPrice(bulkDiscount)}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-gray-600">
                            <span>{t('cart.shipping')}</span>
                            <span className="text-green-600 font-medium">{t('cart.free')}</span>
                          </div>
                          {totalSavings > 0 && (
                            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-semibold text-green-700 flex items-center gap-1">
                                  <i className="ri-trophy-line"></i>
                                  {t('cart.total_saved')}
                                </span>
                                <div className="text-right">
                                  <span className="text-sm font-bold text-green-700 block">
                                    -{formatPrice(totalSavings)}
                                  </span>
                                  <span className="text-[10px] text-green-600">
                                    ≈ -{formatSecondaryPrice(totalSavings)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  <div className="flex justify-between items-center py-6 border-b border-gray-100">
                    <span className="text-lg font-semibold text-gray-900">{t('cart.total')}</span>
                    <div className="text-right">
                      <span className="text-2xl font-bold text-gray-900 block">
                        {formatPrice(total)}
                      </span>
                      <span className="text-xs text-emerald-600 font-medium">
                        ≈ {formatSecondaryPrice(total)} {secondaryLabel}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => setShowCheckout(true)}
                    className="w-full mt-6 bg-gradient-to-r from-[#0078D4] to-[#00a2ed] text-white py-3 rounded-xl font-semibold text-sm sm:text-base hover:from-[#006abc] hover:to-[#0090d4] transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow-xl shadow-blue-500/30 active:scale-95"
                  >
                    {t('cart.proceed_payment')}
                    <i className="ri-arrow-right-line ml-2"></i>
                  </button>

                  <div className="mt-6 text-center">
                    <p className="text-sm text-gray-500 flex items-center justify-center gap-2">
                      <i className="ri-lock-line"></i>
                      {t('cart.secure_payment')}
                    </p>
                    <div className="flex items-center justify-center gap-4 mt-4">
                      <i className="ri-whatsapp-line text-3xl text-green-500"></i>
                      <div className="flex items-center gap-2">
                        <i className="ri-bank-card-2-line text-3xl text-blue-600"></i>
                        <div className="flex items-center gap-2">
                          <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="Mastercard" className="w-6 h-6 object-contain" />
                        </div>
                      </div>
                      <i className="ri-paypal-line text-3xl text-[#0070BA]"></i>
                      <i className="ri-bank-line text-3xl text-gray-400"></i>
                    </div>
                  </div>

                  {/* Garantía */}
                  <div className="mt-6 p-4 border-2 border-dashed border-gray-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-shield-check-line text-green-600 text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-base">
                          {t('cart.satisfaction_guarantee')}
                        </h4>
                        <p className="text-sm text-gray-500 mt-1">
                          {t('cart.guarantee_desc')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Lo que recibirás */}
                  <div className="mt-6 p-5 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                    <h4 className="font-bold text-gray-900 text-base mb-4 flex items-center gap-2">
                      <i className="ri-checkbox-circle-line text-blue-600 text-xl"></i>
                      {t('cart.what_you_receive')}
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <i className="ri-key-2-line text-blue-600 text-lg mt-0.5 flex-shrink-0"></i>
                        <span className="text-sm text-gray-700 leading-relaxed">
                          <strong className="text-gray-900">{t('cart.activation_key')}</strong> {t('cart.original_permanent')}
                        </span>
                      </div>
                      <div className="flex items-start gap-3">
                        <i className="ri-file-text-line text-blue-600 text-lg mt-0.5 flex-shrink-0"></i>
                        <span className="text-sm text-gray-700 leading-relaxed">
                          <strong className="text-gray-900">{t('cart.documentation')}</strong> {t('cart.legal_docs')}
                        </span>
                      </div>
                      <div className="flex items-start gap-3">
                        <i className="ri-bill-line text-blue-600 text-lg mt-0.5 flex-shrink-0"></i>
                        <span className="text-sm text-gray-700 leading-relaxed">
                          <strong className="text-gray-900">{t('cart.invoice')}</strong> {t('cart.electronic_invoice')}
                        </span>
                      </div>
                      <div className="flex items-start gap-3">
                        <i className="ri-download-cloud-2-line text-blue-600 text-lg mt-0.5 flex-shrink-0"></i>
                        <span className="text-sm text-gray-700 leading-relaxed">
                          <strong className="text-gray-900">{t('cart.install_file')}</strong> {t('cart.download')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Código Promocional de Regalo */}
                  {generatedPromoCode && (
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-dashed border-amber-300 rounded-2xl p-5 mb-6 relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-20 h-20 bg-amber-200/30 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                      <div className="absolute bottom-0 left-0 w-16 h-16 bg-orange-200/30 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                      <div className="relative">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <i className="ri-gift-2-line text-amber-600 text-xl"></i>
                          <h4 className="text-base font-bold text-amber-800">{t('cart.thanks_purchase')}</h4>
                        </div>
                        <p className="text-xs text-amber-700 mb-3">
                          {t('cart.promo_desc')}
                        </p>
                        <div className="bg-white rounded-xl p-4 border border-amber-200">
                          <p className="text-xs text-amber-800 font-medium mb-2">
                            <i className="ri-file-list-3-line mr-1"></i>
                            {t('cart.promo_code_label')}: <strong>{generatedPromoCode}</strong>
                          </p>
                          <p className="text-xs text-amber-700 mt-2">
                            {t('cart.promo_email_note')}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Productos Recomendados */}
          {cartItems.length > 0 && <RecommendedProducts />}

        </div>
      </main>

      {/* Beneficios */}
      <section className="py-6 sm:py-12 bg-white border-t border-gray-100">
        <div className="container mx-auto px-3 sm:px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-8">
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-mail-send-line text-[#0078D4] text-sm sm:text-xl"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 text-[10px] sm:text-base leading-tight">{t('benefits.instant_delivery')}</h4>
                <p className="text-[9px] sm:text-xs text-gray-500">{t('benefits.by_email')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-verified-badge-line text-green-600 text-sm sm:text-xl"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 text-[10px] sm:text-base leading-tight">{t('benefits.original')}</h4>
                <p className="text-[9px] sm:text-xs text-gray-500">{t('benefits.genuine')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-customer-service-2-line text-purple-600 text-sm sm:text-xl"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 text-[10px] sm:text-base leading-tight">{t('benefits.support')}</h4>
                <p className="text-[9px] sm:text-xs text-gray-500">{t('benefits.help')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-refund-2-line text-amber-600 text-sm sm:text-xl"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 text-[10px] sm:text-base leading-tight">{t('benefits.guarantee')}</h4>
                <p className="text-[9px] sm:text-xs text-gray-500">{t('benefits.refund')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* ===== BARRA FIJA INFERIOR MÓVIL ===== */}
      {cartItems.length > 0 && !showCheckout && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-200 shadow-[0_-8px_30px_rgba(0,0,0,0.15)] p-4 z-[60] lg:hidden">
          <div className="flex items-center justify-between gap-3 max-w-screen-xl mx-auto">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] text-gray-500 font-medium mb-0.5">{t('cart.total_to_pay')}</p>
              <p className="text-xl font-bold text-gray-900 leading-tight">{formatPrice(total)}</p>
              <p className="text-[10px] text-emerald-600 font-semibold">≈ {formatSecondaryPrice(total)} {secondaryLabel}</p>
            </div>
            <button
              onClick={() => setShowCheckout(true)}
              className="bg-gradient-to-r from-[#0078D4] to-[#00a2ed] text-white px-6 py-3.5 rounded-xl font-bold text-base hover:from-[#006abc] hover:to-[#0090d4] transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 shadow-xl shadow-blue-500/30 active:scale-95"
            >
              {t('cart.pay')}
              <i className="ri-arrow-right-line text-lg"></i>
            </button>
          </div>
        </div>
      )}

      {/* ===== MODAL DE CHECKOUT ===== */}
      {showCheckout && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => !isSubmitting && !orderSuccess && setShowCheckout(false)}
          ></div>

          {/* Modal */}
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            {/* Handle bar mobile */}
            <div className="sm:hidden flex justify-center pt-2 pb-0">
              <div className="w-10 h-1 bg-gray-300 rounded-full"></div>
            </div>

            {/* Header del modal */}
            <div className="sticky top-0 bg-white border-b border-gray-100 px-4 sm:px-6 py-2.5 sm:py-4 rounded-t-2xl flex items-center justify-between z-10">
              <h2 className="text-sm sm:text-xl font-bold text-gray-900">
                {orderSuccess ? t('cart.order_sent') : t('cart.finalize_purchase')}
              </h2>
              {!isSubmitting && (
                <button
                  onClick={() => orderSuccess ? handleCloseSuccess() : setShowCheckout(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 cursor-pointer transition-colors"
                >
                  <i className="ri-close-line text-lg sm:text-xl"></i>
                </button>
              )}
            </div>

            {orderSuccess ? (
              /* Pantalla de éxito */
              <div className="p-4 sm:p-6 text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-5">
                  <i className="ri-check-double-line text-3xl sm:text-4xl text-green-600"></i>
                </div>
                <h3 className="text-lg sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-4">{t('cart.order_sent')}</h3>
                <p className="text-xs sm:text-base text-gray-600 mb-2 sm:mb-4">
                  {t('cart.request_received')}
                </p>
                {paymentMethod === 'whatsapp' && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                    <div className="flex items-start gap-2 sm:gap-3 mb-2 sm:mb-3">
                      <i className="ri-whatsapp-line text-green-600 text-sm sm:text-lg mt-0.5"></i>
                      <div className="text-left">
                        <p className="text-xs sm:text-sm text-green-800 font-medium mb-1">{t('cart.whatsapp_opened')}</p>
                        <p className="text-[10px] sm:text-xs text-green-700">
                          {t('cart.send_payment_to')} <strong>3005461717</strong>. {t('cart.send_receipt')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {paymentMethod === 'nequi' && (
                  <div className="bg-[#E6007E]/5 border border-[#E6007E]/20 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                    <div className="flex items-start gap-2 sm:gap-3 mb-3">
                      <div className="w-5 h-5 sm:w-7 sm:h-7 bg-[#E6007E] rounded-full flex items-center justify-center mt-0.5">
                        <img 
                          src="https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/9bbb0ea2fd6a8b625dfa0d1af5ad1fa4.png" 
                          alt="Nequi" 
                          className="w-5 h-5 object-contain"
                        />
                      </div>
                      <div className="text-left">
                        <p className="text-xs sm:text-sm text-[#E6007E] font-medium mb-1">{t('cart.nequi_pay')}</p>
                        <p className="text-[10px] sm:text-xs text-gray-600">{t('cart.nequi_desc')}</p>
                      </div>
                    </div>
                    
                    {/* Monto a pagar destacado */}
                    <div className="bg-gradient-to-r from-[#E6007E]/10 to-pink-50 border-2 border-[#E6007E]/30 rounded-xl p-3 sm:p-4 mb-3 text-center">
                      <p className="text-[10px] sm:text-xs text-[#E6007E] font-medium mb-1">
                        <i className="ri-money-dollar-circle-line mr-1"></i>
                        {t('cart.amount_to_send')} Nequi
                      </p>
                      <p className="text-xl sm:text-3xl font-bold text-[#E6007E] tracking-tight">
                        {formatColombianPaymentAmount(total).primary}
                      </p>
                      {formatColombianPaymentAmount(total).showEquivalence ? (
                        <div className="mt-1.5 bg-white/60 rounded-lg px-3 py-1.5 inline-block">
                          <p className="text-[9px] sm:text-xs text-gray-600">
                            <i className="ri-exchange-line mr-1"></i>
                            {t('cart.equivalent_to')} <strong className="text-gray-800">{formatColombianPaymentAmount(total).secondary} {formatColombianPaymentAmount(total).secondaryLabel}</strong>
                          </p>
                        </div>
                      ) : (
                        <p className="text-[9px] sm:text-xs text-pink-600 mt-1">
                          ≈ {formatColombianPaymentAmount(total).secondary} {formatColombianPaymentAmount(total).secondaryLabel}
                        </p>
                      )}
                    </div>

                    {/* Número Nequi */}
                    <div className="bg-white rounded-xl p-3 sm:p-4 border border-[#E6007E]/20 mb-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] sm:text-xs text-gray-500">{t('cart.nequi_number')}</span>
                        <span className="text-xs sm:text-sm font-bold text-[#E6007E] tracking-wider">3005461717</span>
                      </div>
                    </div>

                    <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-[10px] sm:text-xs text-amber-800 font-medium text-center">
                        <i className="ri-camera-line mr-1"></i>
                        {t('cart.after_payment')}
                      </p>
                    </div>
                  </div>
                )}

                {paymentMethod === 'efecty' && (
                  <div className="bg-[#FFD100]/10 border border-[#FFD100]/30 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                    <div className="flex items-start gap-2 sm:gap-3 mb-3">
                      <div className="w-5 h-5 sm:w-7 sm:h-7 bg-[#FFD100] rounded-full flex items-center justify-center mt-0.5">
                        <img 
                          src="https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/056c5be3003cf61f3b6a652d0e578a82.png" 
                          alt="Efecty" 
                          className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
                        />
                      </div>
                      <div className="text-left">
                        <p className="text-xs sm:text-sm text-[#D4A800] font-medium mb-1">{t('cart.efecty_pay')}</p>
                        <p className="text-[10px] sm:text-xs text-gray-600">{t('cart.efecty_desc')}</p>
                      </div>
                    </div>
                    
                    {/* Monto a pagar destacado */}
                    <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-[#FFD100]/50 rounded-xl p-3 sm:p-4 mb-3 text-center">
                      <p className="text-[10px] sm:text-xs text-[#D4A800] font-medium mb-1">
                        <i className="ri-money-dollar-circle-line mr-1"></i>
                        {t('cart.amount_to_deposit')}
                      </p>
                      <p className="text-xl sm:text-3xl font-bold text-[#D4A800] tracking-tight">
                        {formatColombianPaymentAmount(total).primary}
                      </p>
                      {formatColombianPaymentAmount(total).showEquivalence ? (
                        <div className="mt-1.5 bg-white/60 rounded-lg px-3 py-1.5 inline-block">
                          <p className="text-[9px] sm:text-xs text-gray-600">
                            <i className="ri-exchange-line mr-1"></i>
                            {t('cart.equivalent_to')} <strong className="text-gray-800">{formatColombianPaymentAmount(total).secondary} {formatColombianPaymentAmount(total).secondaryLabel}</strong>
                          </p>
                        </div>
                      ) : (
                        <p className="text-[9px] sm:text-xs text-amber-600 mt-1">
                          ≈ {formatColombianPaymentAmount(total).secondary} {formatColombianPaymentAmount(total).secondaryLabel}
                        </p>
                      )}
                    </div>

                    {/* Instrucciones Efecty */}
                    <div className="bg-white rounded-xl p-3 sm:p-4 border border-[#FFD100]/30 mb-3">
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <i className="ri-store-2-line text-[#D4A800] text-sm mt-0.5"></i>
                          <div>
                            <p className="text-[10px] sm:text-xs text-gray-700 font-medium">{t('cart.efecty_via_nequi')}</p>
                            <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">Número: <strong>3005461717</strong></p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-[10px] sm:text-xs text-amber-800 font-medium text-center">
                        <i className="ri-camera-line mr-1"></i>
                        {t('cart.after_payment')}
                      </p>
                    </div>
                  </div>
                )}

                {paymentMethod === 'daviplata' && (
                  <div className="bg-[#ED1C24]/5 border border-[#ED1C24]/20 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                    <div className="flex items-start gap-2 sm:gap-3 mb-3">
                      <div className="w-5 h-5 sm:w-7 sm:h-7 bg-[#ED1C24] rounded-full flex items-center justify-center mt-0.5">
                        <img 
                          src="https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/4738bf97cbdee510c76b19003d71dd2e.png" 
                          alt="Daviplata" 
                          className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
                        />
                      </div>
                      <div className="text-left">
                        <p className="text-xs sm:text-sm text-[#ED1C24] font-medium mb-1">{t('cart.daviplata_pay')}</p>
                        <p className="text-[10px] sm:text-xs text-gray-600">{t('cart.daviplata_desc')}</p>
                      </div>
                    </div>
                    
                    {/* Monto a pagar destacado */}
                    <div className="bg-gradient-to-r from-red-50 to-pink-50 border-2 border-[#ED1C24]/30 rounded-xl p-3 sm:p-4 mb-3 text-center">
                      <p className="text-[10px] sm:text-xs text-[#ED1C24] font-medium mb-1">
                        <i className="ri-money-dollar-circle-line mr-1"></i>
                        {t('cart.amount_to_send')} Daviplata
                      </p>
                      <p className="text-xl sm:text-3xl font-bold text-[#ED1C24] tracking-tight">
                        {formatColombianPaymentAmount(total).primary}
                      </p>
                      {formatColombianPaymentAmount(total).showEquivalence ? (
                        <div className="mt-1.5 bg-white/60 rounded-lg px-3 py-1.5 inline-block">
                          <p className="text-[9px] sm:text-xs text-gray-600">
                            <i className="ri-exchange-line mr-1"></i>
                            {t('cart.equivalent_to')} <strong className="text-gray-800">{formatColombianPaymentAmount(total).secondary} {formatColombianPaymentAmount(total).secondaryLabel}</strong>
                          </p>
                        </div>
                      ) : (
                        <p className="text-[9px] sm:text-xs text-red-600 mt-1">
                          ≈ {formatColombianPaymentAmount(total).secondary} {formatColombianPaymentAmount(total).secondaryLabel}
                        </p>
                      )}
                    </div>

                    {/* Número Daviplata */}
                    <div className="bg-white rounded-xl p-3 sm:p-4 border border-[#ED1C24]/20 mb-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] sm:text-xs text-gray-500">{t('cart.daviplata_number')}</span>
                        <span className="text-xs sm:text-sm font-bold text-[#ED1C24] tracking-wider">3005461717</span>
                      </div>
                    </div>

                    <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-[10px] sm:text-xs text-amber-800 font-medium text-center">
                        <i className="ri-camera-line mr-1"></i>
                        {t('cart.after_payment')}
                      </p>
                    </div>
                  </div>
                )}

                {paymentMethod === 'mercadopago' && (
                  <div className="bg-sky-50 border border-sky-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                    <div className="flex items-start gap-2 sm:gap-3 mb-2 sm:mb-3">
                      <i className="ri-information-line text-sky-600 text-sm sm:text-lg mt-0.5"></i>
                      <div className="text-left">
                        <p className="text-xs sm:text-sm text-sky-800 font-medium mb-1">{t('cart.mercadopago_pay')}</p>
                        <p className="text-[10px] sm:text-xs text-sky-700 mt-0.5 sm:mt-1">
                          {t('cart.mercadopago_desc')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {paymentMethod === 'transferencia' && (
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                    <div className="flex items-start gap-2 sm:gap-3 mb-3">
                      <div className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <img 
                          src="https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/d8f982c8f97c6828001f5273e10565ff.png" 
                          alt="Bancolombia" 
                          className="w-6 h-6 sm:w-8 sm:h-8 object-contain"
                        />
                      </div>
                      <div className="text-left">
                        <p className="text-xs sm:text-sm text-blue-800 font-bold mb-1">{t('cart.transfer_pay')}</p>
                        <p className="text-[10px] sm:text-xs text-gray-600">
                          {t('cart.transfer_desc')}
                        </p>
                      </div>
                    </div>
                    
                    {/* Monto a pagar destacado */}
                    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-xl p-3 sm:p-4 mb-3 text-center">
                      <p className="text-[10px] sm:text-xs text-emerald-700 font-medium mb-1">
                        <i className="ri-money-dollar-circle-line mr-1"></i>
                        {t('cart.amount_to_transfer')}
                      </p>
                      <p className="text-xl sm:text-3xl font-bold text-emerald-700 tracking-tight">
                        {formatColombianPaymentAmount(total).primary}
                      </p>
                      {formatColombianPaymentAmount(total).showEquivalence ? (
                        <div className="mt-1.5 bg-white/60 rounded-lg px-3 py-1.5 inline-block">
                          <p className="text-[9px] sm:text-xs text-gray-600">
                            <i className="ri-exchange-line mr-1"></i>
                            {t('cart.equivalent_to')} <strong className="text-gray-800">{formatColombianPaymentAmount(total).secondary} {formatColombianPaymentAmount(total).secondaryLabel}</strong>
                          </p>
                        </div>
                      ) : (
                        <p className="text-[9px] sm:text-xs text-emerald-600 mt-1">
                          ≈ {formatColombianPaymentAmount(total).secondary} {formatColombianPaymentAmount(total).secondaryLabel}
                        </p>
                      )}
                    </div>

                    {/* Datos de la cuenta */}
                    <div className="bg-white rounded-xl p-3 sm:p-4 border border-blue-200 mb-3">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] sm:text-xs text-gray-500">{t('cart.bank')}</span>
                          <span className="text-[10px] sm:text-xs font-semibold text-gray-900">Bancolombia</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] sm:text-xs text-gray-500">{t('cart.account_type')}</span>
                          <span className="text-[10px] sm:text-xs font-semibold text-gray-900">{t('cart.savings_account')}</span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[10px] sm:text-xs text-gray-500">{t('cart.account_number')}</span>
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs sm:text-sm font-bold text-blue-700 tracking-wider">363-524841-69</span>
                            <button
                              onClick={copyAccountNumber}
                              className={`px-2 py-1 rounded-lg text-[9px] sm:text-[10px] font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center gap-0.5 ${
                                accountCopied
                                  ? 'bg-green-100 text-green-700 border border-green-200'
                                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200 border border-blue-200'
                              }`}
                            >
                              {accountCopied ? (
                                <><i className="ri-check-line"></i>{t('cart.copied')}</>
                              ) : (
                                <><i className="ri-file-copy-line"></i>{t('cart.copy')}</>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* QR Code */}
                    <div className="bg-white rounded-xl p-3 sm:p-4 border border-blue-200 text-center">
                      <p className="text-[10px] sm:text-xs text-gray-500 mb-2 font-medium">
                        <i className="ri-qr-code-line mr-1"></i>
                        {t('cart.scan_qr')}
                      </p>
                      <div className="w-36 h-36 sm:w-48 sm:h-48 mx-auto rounded-lg overflow-hidden border border-gray-200">
                        <img 
                          src="https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/347934ce9fc22086f1523c6a10eb4293.jpeg" 
                          alt="QR Bancolombia" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <p className="text-[9px] sm:text-[10px] text-gray-400 mt-2">{t('cart.digital_license_payment')}</p>
                    </div>

                    <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-[10px] sm:text-xs text-amber-800 font-medium text-center">
                        <i className="ri-camera-line mr-1"></i>
                        {t('cart.after_payment')}
                      </p>
                    </div>

                    {/* Botón WhatsApp para enviar comprobante */}
                    <a
                      href={`https://wa.me/79534940353?text=${encodeURIComponent(buildTransferWhatsAppMessage())}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-green-500 text-white text-xs sm:text-sm font-semibold px-4 py-2.5 rounded-xl hover:bg-green-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-whatsapp-line text-base sm:text-lg"></i>
                      {t('cart.send_receipt_whatsapp')}
                    </a>
                  </div>
                )}

                {paymentMethod === 'paypal' && (
                  <div className="bg-[#0070BA]/5 border border-[#0070BA]/20 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                    <div className="flex items-start gap-2 sm:gap-3 mb-2 sm:mb-3">
                      <i className="ri-information-line text-[#0070BA] text-sm sm:text-lg mt-0.5"></i>
                      <div className="text-left">
                        <p className="text-xs sm:text-sm text-[#0070BA] font-medium mb-1">{t('cart.paypal_pay')}</p>
                        <p className="text-[10px] sm:text-xs text-[#0070BA] mt-0.5 sm:mt-1">
                          {t('cart.paypal_desc')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  onClick={handleCloseSuccess}
                  className="w-full bg-gradient-to-r from-[#0078D4] to-[#00a2ed] text-white py-3 rounded-xl font-semibold text-sm sm:text-base hover:from-[#006abc] hover:to-[#0090d4] transition-all cursor-pointer whitespace-nowrap"
                >
                  {t('cart.back_to_store')}
                </button>
              </div>
            ) : (
              /* Formulario de checkout */
              <form
                id="checkout-form"
                ref={formRef}
                data-readdy-form
                onSubmit={handleCheckoutSubmit}
                className="p-3 sm:p-6 space-y-3 sm:space-y-6"
              >
                {/* Resumen rápido */}
                <div className="bg-gray-50 rounded-xl p-2.5 sm:p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] sm:text-sm text-gray-600">{cartItems.length} {t('cart.products_count')}</span>
                    <div className="text-right">
                      <span className="text-sm sm:text-lg font-bold text-gray-900 block">{formatPrice(total)}</span>
                      <span className="text-[9px] sm:text-xs text-emerald-600 font-medium">≈ {formatSecondaryPrice(total)} {secondaryLabel}</span>
                    </div>
                  </div>
                </div>

                {/* Método de pago */}
                <div>
                  <label className="block text-[10px] sm:text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
                    {t('cart.payment_method')}
                  </label>
                  <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-2 sm:gap-3">
                    {/* Mercado Pago / Tarjeta */}
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('mercadopago')}
                      className={`p-2 sm:p-4 rounded-xl border-2 cursor-pointer flex flex-col items-center gap-0.5 sm:gap-2 transition-all duration-300 relative ${
                        paymentMethod === 'mercadopago'
                          ? 'border-sky-500 bg-sky-50 shadow-lg shadow-sky-500/15 scale-[1.03] ring-2 ring-sky-500/20'
                          : paymentMethod
                            ? 'border-gray-200 bg-white opacity-60 grayscale-[30%] hover:opacity-100 hover:grayscale-0 hover:border-gray-300'
                            : 'border-gray-200 hover:border-gray-300 bg-white hover:shadow-md'
                      }`}
                    >
                      <div className={`w-8 h-8 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                        paymentMethod === 'mercadopago' ? 'bg-sky-500 animate-[bounceSelect_0.4s_ease-out]' : 'bg-gray-100'
                      }`}>
                        <i className={`ri-bank-card-2-line text-base sm:text-2xl transition-colors duration-300 ${
                          paymentMethod === 'mercadopago' ? 'text-white' : 'text-sky-500'
                        }`}></i>
                      </div>
                      <span className={`text-[9px] sm:text-sm font-semibold leading-tight transition-colors duration-300 ${
                        paymentMethod === 'mercadopago' ? 'text-sky-700' : 'text-gray-700'
                      }`}>Tarjeta</span>
                      {paymentMethod === 'mercadopago' && (
                        <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-sky-500 rounded-full flex items-center justify-center animate-[popIn_0.3s_ease-out] shadow-sm">
                          <i className="ri-check-line text-white text-[10px]"></i>
                        </span>
                      )}
                    </button>

                    {/* PayPal */}
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('paypal')}
                      className={`p-2 sm:p-4 rounded-xl border-2 cursor-pointer flex flex-col items-center gap-0.5 sm:gap-2 transition-all duration-300 relative ${
                        paymentMethod === 'paypal'
                          ? 'border-[#0070BA] bg-[#0070BA]/5 shadow-lg shadow-[#0070BA]/15 scale-[1.03] ring-2 ring-[#0070BA]/20'
                          : paymentMethod
                            ? 'border-gray-200 bg-white opacity-60 grayscale-[30%] hover:opacity-100 hover:grayscale-0 hover:border-gray-300'
                            : 'border-gray-200 hover:border-gray-300 bg-white hover:shadow-md'
                      }`}
                    >
                      <div className={`w-8 h-8 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                        paymentMethod === 'paypal' ? 'bg-[#0070BA] animate-[bounceSelect_0.4s_ease-out]' : 'bg-gray-100'
                      }`}>
                        <i className={`ri-paypal-line text-base sm:text-2xl transition-colors duration-300 ${
                          paymentMethod === 'paypal' ? 'text-white' : 'text-[#0070BA]'
                        }`}></i>
                      </div>
                      <span className={`text-[9px] sm:text-sm font-semibold leading-tight transition-colors duration-300 ${
                        paymentMethod === 'paypal' ? 'text-[#0070BA]' : 'text-gray-700'
                      }`}>PayPal</span>
                      {paymentMethod === 'paypal' && (
                        <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-[#0070BA] rounded-full flex items-center justify-center animate-[popIn_0.3s_ease-out] shadow-sm">
                          <i className="ri-check-line text-white text-[10px]"></i>
                        </span>
                      )}
                    </button>

                    {/* Transferencia */}
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('transferencia')}
                      className={`p-2 sm:p-4 rounded-xl border-2 cursor-pointer flex flex-col items-center gap-0.5 sm:gap-2 transition-all duration-300 relative ${
                        paymentMethod === 'transferencia'
                          ? 'border-[#0078D4] bg-blue-50 shadow-lg shadow-[#0078D4]/15 scale-[1.03] ring-2 ring-[#0078D4]/20'
                          : paymentMethod
                            ? 'border-gray-200 bg-white opacity-60 grayscale-[30%] hover:opacity-100 hover:grayscale-0 hover:border-gray-300'
                            : 'border-gray-200 hover:border-gray-300 bg-white hover:shadow-md'
                      }`}
                    >
                      <div className={`w-8 h-8 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                        paymentMethod === 'transferencia' ? 'bg-[#0078D4] animate-[bounceSelect_0.4s_ease-out]' : 'bg-gray-100'
                      }`}>
                        <i className={`ri-bank-line text-base sm:text-2xl transition-colors duration-300 ${
                          paymentMethod === 'transferencia' ? 'text-white' : 'text-[#0078D4]'
                        }`}></i>
                      </div>
                      <span className={`text-[9px] sm:text-sm font-semibold leading-tight transition-colors duration-300 ${
                        paymentMethod === 'transferencia' ? 'text-[#0078D4]' : 'text-gray-700'
                      }`}>Transfer.</span>
                      {paymentMethod === 'transferencia' && (
                        <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-[#0078D4] rounded-full flex items-center justify-center animate-[popIn_0.3s_ease-out] shadow-sm">
                          <i className="ri-check-line text-white text-[10px]"></i>
                        </span>
                      )}
                    </button>

                    {/* WhatsApp */}
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('whatsapp')}
                      className={`p-2 sm:p-4 rounded-xl border-2 cursor-pointer flex flex-col items-center gap-0.5 sm:gap-2 transition-all duration-300 ${
                        paymentMethod === 'whatsapp'
                          ? 'border-green-500 bg-green-50 shadow-lg shadow-green-500/15 scale-[1.03] ring-2 ring-green-500/20'
                          : paymentMethod
                            ? 'border-gray-200 bg-white opacity-60 grayscale-[30%] hover:opacity-100 hover:grayscale-0 hover:border-gray-300'
                            : 'border-gray-200 hover:border-gray-300 bg-white hover:shadow-md'
                      }`}
                    >
                      <div className={`w-8 h-8 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                        paymentMethod === 'whatsapp' ? 'bg-green-500 animate-[bounceSelect_0.4s_ease-out]' : 'bg-gray-100'
                      }`}>
                        <i className={`ri-whatsapp-line text-base sm:text-2xl transition-colors duration-300 ${
                          paymentMethod === 'whatsapp' ? 'text-white' : 'text-green-500'
                        }`}></i>
                      </div>
                      <span className={`text-[9px] sm:text-sm font-semibold leading-tight transition-colors duration-300 ${
                        paymentMethod === 'whatsapp' ? 'text-green-700' : 'text-gray-700'
                      }`}>WhatsApp</span>
                      {paymentMethod === 'whatsapp' && (
                        <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center animate-[popIn_0.3s_ease-out] shadow-sm">
                          <i className="ri-check-line text-white text-[10px]"></i>
                        </span>
                      )}
                    </button>

                    {/* Nequi */}
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('nequi')}
                      className={`p-2 sm:p-4 rounded-xl border-2 cursor-pointer flex flex-col items-center gap-0.5 sm:gap-2 transition-all duration-300 ${
                        paymentMethod === 'nequi'
                          ? 'border-[#E6007E] bg-[#E6007E]/5 shadow-lg shadow-[#E6007E]/15 scale-[1.03] ring-2 ring-[#E6007E]/20'
                          : paymentMethod
                            ? 'border-gray-200 bg-white opacity-60 grayscale-[30%] hover:opacity-100 hover:grayscale-0 hover:border-gray-300'
                            : 'border-gray-200 hover:border-gray-300 bg-white hover:shadow-md'
                      }`}
                    >
                      <div className={`w-8 h-8 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                        paymentMethod === 'nequi' ? 'bg-[#E6007E] animate-[bounceSelect_0.4s_ease-out]' : 'bg-gray-100'
                      }`}>
                        <img 
                          src="https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/9bbb0ea2fd6a8b625dfa0d1af5ad1fa4.png" 
                          alt="Nequi" 
                          className="w-5 h-5 object-contain"
                        />
                      </div>
                      <span className={`text-[9px] sm:text-sm font-semibold leading-tight transition-colors duration-300 ${
                        paymentMethod === 'nequi' ? 'text-[#E6007E]' : 'text-gray-700'
                      }`}>Nequi</span>
                      {paymentMethod === 'nequi' && (
                        <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-[#E6007E] rounded-full flex items-center justify-center animate-[popIn_0.3s_ease-out] shadow-sm">
                          <i className="ri-check-line text-white text-[10px]"></i>
                        </span>
                      )}
                    </button>

                    {/* Efecty */}
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('efecty')}
                      className={`p-2 sm:p-4 rounded-xl border-2 cursor-pointer flex flex-col items-center gap-0.5 sm:gap-2 transition-all duration-300 ${
                        paymentMethod === 'efecty'
                          ? 'border-[#FFD100] bg-[#FFD100]/10 shadow-lg shadow-[#FFD100]/15 scale-[1.03] ring-2 ring-[#FFD100]/20'
                          : paymentMethod
                            ? 'border-gray-200 bg-white opacity-60 grayscale-[30%] hover:opacity-100 hover:grayscale-0 hover:border-gray-300'
                            : 'border-gray-200 hover:border-gray-300 bg-white hover:shadow-md'
                      }`}
                    >
                      <div className={`w-8 h-8 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                        paymentMethod === 'efecty' ? 'bg-[#FFD100] animate-[bounceSelect_0.4s_ease-out]' : 'bg-gray-100'
                      }`}>
                        <img 
                          src="https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/056c5be3003cf61f3b6a652d0e578a82.png" 
                          alt="Efecty" 
                          className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
                        />
                      </div>
                      <span className={`text-[9px] sm:text-sm font-semibold leading-tight transition-colors duration-300 ${
                        paymentMethod === 'efecty' ? 'text-[#D4A800]' : 'text-gray-700'
                      }`}>Efecty</span>
                      {paymentMethod === 'efecty' && (
                        <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-[#FFD100] rounded-full flex items-center justify-center animate-[popIn_0.3s_ease-out] shadow-sm">
                          <i className="ri-check-line text-gray-900 text-[10px]"></i>
                        </span>
                      )}
                    </button>

                    {/* Daviplata */}
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('daviplata')}
                      className={`p-2 sm:p-4 rounded-xl border-2 cursor-pointer flex flex-col items-center gap-0.5 sm:gap-2 transition-all duration-300 ${
                        paymentMethod === 'daviplata'
                          ? 'border-[#ED1C24] bg-[#ED1C24]/5 shadow-lg shadow-[#ED1C24]/15 scale-[1.03] ring-2 ring-[#ED1C24]/20'
                          : paymentMethod
                            ? 'border-gray-200 bg-white opacity-60 grayscale-[30%] hover:opacity-100 hover:grayscale-0 hover:border-gray-300'
                            : 'border-gray-200 hover:border-gray-300 bg-white hover:shadow-md'
                      }`}
                    >
                      <div className={`w-8 h-8 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                        paymentMethod === 'daviplata' ? 'bg-[#ED1C24] animate-[bounceSelect_0.4s_ease-out]' : 'bg-gray-100'
                      }`}>
                        <img 
                          src="https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/4738bf97cbdee510c76b19003d71dd2e.png" 
                          alt="Daviplata" 
                          className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
                        />
                      </div>
                      <span className={`text-[9px] sm:text-sm font-semibold leading-tight transition-colors duration-300 ${
                        paymentMethod === 'daviplata' ? 'text-[#ED1C24]' : 'text-gray-700'
                      }`}>Daviplata</span>
                      {paymentMethod === 'daviplata' && (
                        <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-[#ED1C24] rounded-full flex items-center justify-center animate-[popIn_0.3s_ease-out] shadow-sm">
                          <i className="ri-check-line text-white text-[10px]"></i>
                        </span>
                      )}
                    </button>
                  </div>
                </div>

                {/* Datos del cliente */}
                <div className="space-y-2.5 sm:space-y-4">
                  <label className="block text-[10px] sm:text-sm font-semibold text-gray-900 mb-1.5 sm:mb-3">
                    {t('cart.your_data')}
                  </label>

                  <div>
                    <div className="relative">
                      <i className="ri-user-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                      <input
                        type="text"
                        name="nombre"
                        value={customerName}
                        onChange={(e) => {
                          setCustomerName(e.target.value);
                          handleChange('customerName', e.target.value);
                        }}
                        onBlur={() => handleBlur('customerName')}
                        placeholder={t('cart.full_name')}
                        required
                        className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 border rounded-xl focus:outline-none focus:ring-2 text-xs sm:text-sm ${
                          touched.customerName && errors.customerName
                            ? 'border-red-300 focus:ring-red-500'
                            : 'border-gray-200 focus:ring-[#0078D4]'
                        }`}
                      />
                    </div>
                    {touched.customerName && errors.customerName && (
                      <p className="text-red-500 text-[10px] sm:text-xs mt-1 ml-1">
                        <i className="ri-error-warning-line mr-1"></i>
                        {errors.customerName}
                      </p>
                    )}
                  </div>

                  <div>
                    <div className="relative">
                      <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                      <input
                        type="email"
                        name="email"
                        value={customerEmail}
                        onChange={(e) => {
                          setCustomerEmail(e.target.value);
                          handleChange('customerEmail', e.target.value);
                        }}
                        onBlur={() => handleBlur('customerEmail')}
                        placeholder={t('cart.email_placeholder')}
                        required
                        className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 border rounded-xl focus:outline-none focus:ring-2 text-xs sm:text-sm ${
                          touched.customerEmail && errors.customerEmail
                            ? 'border-red-300 focus:ring-red-500'
                            : 'border-gray-200 focus:ring-[#0078D4]'
                        }`}
                      />
                    </div>
                    {touched.customerEmail && errors.customerEmail ? (
                      <p className="text-red-500 text-[10px] sm:text-xs mt-1 ml-1">
                        <i className="ri-error-warning-line mr-1"></i>
                        {errors.customerEmail}
                      </p>
                    ) : (
                      <p className="text-[9px] sm:text-xs text-gray-400 mt-0.5 sm:mt-1 ml-1">{t('cart.email_note')}</p>
                    )}
                  </div>

                  <div>
                    <div className="relative">
                      <i className="ri-phone-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                      <input
                        type="tel"
                        name="telefono"
                        value={customerPhone}
                        onChange={(e) => {
                          setCustomerPhone(e.target.value);
                          handleChange('customerPhone', e.target.value);
                        }}
                        onBlur={() => handleBlur('customerPhone')}
                        placeholder={t('cart.phone_placeholder')}
                        required
                        className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 border rounded-xl focus:outline-none focus:ring-2 text-xs sm:text-sm ${
                          touched.customerPhone && errors.customerPhone
                            ? 'border-red-300 focus:ring-red-500'
                            : 'border-gray-200 focus:ring-[#0078D4]'
                        }`}
                      />
                    </div>
                    {touched.customerPhone && errors.customerPhone && (
                      <p className="text-red-500 text-[10px] sm:text-xs mt-1 ml-1">
                        <i className="ri-error-warning-line mr-1"></i>
                        {errors.customerPhone}
                      </p>
                    )}
                  </div>

                  <div>
                    <div className="relative">
                      <i className="ri-chat-1-line absolute left-3 top-2.5 sm:top-3 text-gray-400 text-sm"></i>
                      <textarea
                        name="notas"
                        value={customerNotes}
                        onChange={(e) => {
                          if (e.target.value.length <= 500) {
                            setCustomerNotes(e.target.value);
                            handleChange('customerNotes', e.target.value);
                          }
                        }}
                        onBlur={() => handleBlur('customerNotes')}
                        placeholder={t('cart.notes_placeholder')}
                        maxLength={500}
                        rows={2}
                        className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 border rounded-xl focus:outline-none focus:ring-2 text-xs sm:text-sm resize-none ${
                          touched.customerNotes && errors.customerNotes
                            ? 'border-red-300 focus:ring-red-500'
                            : 'border-gray-200 focus:ring-[#0078D4]'
                        }`}
                      ></textarea>
                    </div>
                    <div className="flex justify-between items-center mt-0.5 sm:mt-1">
                      {touched.customerNotes && errors.customerNotes ? (
                        <p className="text-red-500 text-[10px] sm:text-xs ml-1">
                          <i className="ri-error-warning-line mr-1"></i>
                          {errors.customerNotes}
                        </p>
                      ) : (
                        <span></span>
                      )}
                      <p className="text-[9px] sm:text-xs text-gray-400 ml-1">{customerNotes.length}/500</p>
                    </div>
                  </div>
                </div>

                {/* Info del método seleccionado */}
                {paymentMethod === 'whatsapp' && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                    <div className="flex items-start gap-2 sm:gap-3 mb-2 sm:mb-3">
                      <i className="ri-information-line text-green-600 text-sm sm:text-lg mt-0.5"></i>
                      <div className="text-left">
                        <p className="text-xs sm:text-sm text-green-800 font-medium mb-1">{t('cart.whatsapp_opened')}</p>
                        <p className="text-[10px] sm:text-xs text-green-700 mt-0.5 sm:mt-1">
                          {t('cart.whatsapp_desc')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {paymentMethod === 'nequi' && (
                  <div className="bg-[#E6007E]/5 border border-[#E6007E]/20 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                    <div className="flex items-start gap-2 sm:gap-3 mb-3">
                      <div className="w-5 h-5 sm:w-7 sm:h-7 bg-[#E6007E] rounded-full flex items-center justify-center mt-0.5">
                        <img 
                          src="https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/9bbb0ea2fd6a8b625dfa0d1af5ad1fa4.png" 
                          alt="Nequi" 
                          className="w-5 h-5 object-contain"
                        />
                      </div>
                      <div className="text-left">
                        <p className="text-xs sm:text-sm text-[#E6007E] font-medium mb-1">{t('cart.nequi_pay')}</p>
                        <p className="text-[10px] sm:text-xs text-gray-600">{t('cart.nequi_desc')}</p>
                      </div>
                    </div>
                    
                    {/* Monto a pagar destacado */}
                    <div className="bg-gradient-to-r from-[#E6007E]/10 to-pink-50 border-2 border-[#E6007E]/30 rounded-xl p-3 sm:p-4 mb-3 text-center">
                      <p className="text-[10px] sm:text-xs text-[#E6007E] font-medium mb-1">
                        <i className="ri-money-dollar-circle-line mr-1"></i>
                        {t('cart.amount_to_send')} Nequi
                      </p>
                      <p className="text-xl sm:text-3xl font-bold text-[#E6007E] tracking-tight">
                        {formatColombianPaymentAmount(total).primary}
                      </p>
                      {formatColombianPaymentAmount(total).showEquivalence ? (
                        <div className="mt-1.5 bg-white/60 rounded-lg px-3 py-1.5 inline-block">
                          <p className="text-[9px] sm:text-xs text-gray-600">
                            <i className="ri-exchange-line mr-1"></i>
                            {t('cart.equivalent_to')} <strong className="text-gray-800">{formatColombianPaymentAmount(total).secondary} {formatColombianPaymentAmount(total).secondaryLabel}</strong>
                          </p>
                        </div>
                      ) : (
                        <p className="text-[9px] sm:text-xs text-pink-600 mt-1">
                          ≈ {formatColombianPaymentAmount(total).secondary} {formatColombianPaymentAmount(total).secondaryLabel}
                        </p>
                      )}
                    </div>

                    {/* Número Nequi */}
                    <div className="bg-white rounded-xl p-3 sm:p-4 border border-[#E6007E]/20 mb-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] sm:text-xs text-gray-500">{t('cart.nequi_number')}</span>
                        <span className="text-xs sm:text-sm font-bold text-[#E6007E] tracking-wider">3005461717</span>
                      </div>
                    </div>

                    <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-[10px] sm:text-xs text-amber-800 font-medium text-center">
                        <i className="ri-camera-line mr-1"></i>
                        {t('cart.after_payment')}
                      </p>
                    </div>
                  </div>
                )}

                {paymentMethod === 'efecty' && (
                  <div className="bg-[#FFD100]/10 border border-[#FFD100]/30 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                    <div className="flex items-start gap-2 sm:gap-3 mb-3">
                      <div className="w-5 h-5 sm:w-7 sm:h-7 bg-[#FFD100] rounded-full flex items-center justify-center mt-0.5">
                        <img 
                          src="https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/056c5be3003cf61f3b6a652d0e578a82.png" 
                          alt="Efecty" 
                          className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
                        />
                      </div>
                      <div className="text-left">
                        <p className="text-xs sm:text-sm text-[#D4A800] font-medium mb-1">{t('cart.efecty_pay')}</p>
                        <p className="text-[10px] sm:text-xs text-gray-600">
                          {t('cart.efecty_desc')}
                        </p>
                      </div>
                    </div>
                    
                    {/* Monto a pagar destacado */}
                    <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-[#FFD100]/50 rounded-xl p-3 sm:p-4 mb-3 text-center">
                      <p className="text-[10px] sm:text-xs text-[#D4A800] font-medium mb-1">
                        <i className="ri-money-dollar-circle-line mr-1"></i>
                        {t('cart.amount_to_deposit')}
                      </p>
                      <p className="text-xl sm:text-3xl font-bold text-[#D4A800] tracking-tight">
                        {formatColombianPaymentAmount(total).primary}
                      </p>
                      {formatColombianPaymentAmount(total).showEquivalence ? (
                        <div className="mt-1.5 bg-white/60 rounded-lg px-3 py-1.5 inline-block">
                          <p className="text-[9px] sm:text-xs text-gray-600">
                            <i className="ri-exchange-line mr-1"></i>
                            {t('cart.equivalent_to')} <strong className="text-gray-800">{formatColombianPaymentAmount(total).secondary} {formatColombianPaymentAmount(total).secondaryLabel}</strong>
                          </p>
                        </div>
                      ) : (
                        <p className="text-[9px] sm:text-xs text-amber-600 mt-1">
                          ≈ {formatColombianPaymentAmount(total).secondary} {formatColombianPaymentAmount(total).secondaryLabel}
                        </p>
                      )}
                    </div>

                    {/* Instrucciones Efecty */}
                    <div className="bg-white rounded-xl p-3 sm:p-4 border border-[#FFD100]/30 mb-3">
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <i className="ri-store-2-line text-[#D4A800] text-sm mt-0.5"></i>
                          <div>
                            <p className="text-[10px] sm:text-xs text-gray-700 font-medium">{t('cart.efecty_via_nequi')}</p>
                            <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">Número: <strong>3005461717</strong></p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-[10px] sm:text-xs text-amber-800 font-medium text-center">
                        <i className="ri-camera-line mr-1"></i>
                        {t('cart.after_payment')}
                      </p>
                    </div>
                  </div>
                )}

                {paymentMethod === 'daviplata' && (
                  <div className="bg-[#ED1C24]/5 border border-[#ED1C24]/20 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                    <div className="flex items-start gap-2 sm:gap-3 mb-3">
                      <div className="w-5 h-5 sm:w-7 sm:h-7 bg-[#ED1C24] rounded-full flex items-center justify-center mt-0.5">
                        <img 
                          src="https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/4738bf97cbdee510c76b19003d71dd2e.png" 
                          alt="Daviplata" 
                          className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
                        />
                      </div>
                      <div className="text-left">
                        <p className="text-xs sm:text-sm text-[#ED1C24] font-medium mb-1">{t('cart.daviplata_pay')}</p>
                        <p className="text-[10px] sm:text-xs text-gray-600">
                          {t('cart.daviplata_desc')}
                        </p>
                      </div>
                    </div>
                    
                    {/* Monto a pagar destacado */}
                    <div className="bg-gradient-to-r from-red-50 to-pink-50 border-2 border-[#ED1C24]/30 rounded-xl p-3 sm:p-4 mb-3 text-center">
                      <p className="text-[10px] sm:text-xs text-[#ED1C24] font-medium mb-1">
                        <i className="ri-money-dollar-circle-line mr-1"></i>
                        {t('cart.amount_to_send')} Daviplata
                      </p>
                      <p className="text-xl sm:text-3xl font-bold text-[#ED1C24] tracking-tight">
                        {formatColombianPaymentAmount(total).primary}
                      </p>
                      {formatColombianPaymentAmount(total).showEquivalence ? (
                        <div className="mt-1.5 bg-white/60 rounded-lg px-3 py-1.5 inline-block">
                          <p className="text-[9px] sm:text-xs text-gray-600">
                            <i className="ri-exchange-line mr-1"></i>
                            {t('cart.equivalent_to')} <strong className="text-gray-800">{formatColombianPaymentAmount(total).secondary} {formatColombianPaymentAmount(total).secondaryLabel}</strong>
                          </p>
                        </div>
                      ) : (
                        <p className="text-[9px] sm:text-xs text-red-600 mt-1">
                          ≈ {formatColombianPaymentAmount(total).secondary} {formatColombianPaymentAmount(total).secondaryLabel}
                        </p>
                      )}
                    </div>

                    {/* Número Daviplata */}
                    <div className="bg-white rounded-xl p-3 sm:p-4 border border-[#ED1C24]/20 mb-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] sm:text-xs text-gray-500">{t('cart.daviplata_number')}</span>
                        <span className="text-xs sm:text-sm font-bold text-[#ED1C24] tracking-wider">3005461717</span>
                      </div>
                    </div>

                    <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-[10px] sm:text-xs text-amber-800 font-medium text-center">
                        <i className="ri-camera-line mr-1"></i>
                        {t('cart.after_payment')}
                      </p>
                    </div>
                  </div>
                )}

                {paymentMethod === 'mercadopago' && (
                  <div className="bg-sky-50 border border-sky-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                    <div className="flex items-start gap-2 sm:gap-3 mb-2 sm:mb-3">
                      <i className="ri-information-line text-sky-600 text-sm sm:text-lg mt-0.5"></i>
                      <div className="text-left">
                        <p className="text-xs sm:text-sm text-sky-800 font-medium mb-1">{t('cart.mercadopago_pay')}</p>
                        <p className="text-[10px] sm:text-xs text-sky-700 mt-0.5 sm:mt-1">
                          {t('cart.mercadopago_desc')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {paymentMethod === 'transferencia' && (
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                    <div className="flex items-start gap-2 sm:gap-3 mb-3">
                      <div className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <img 
                          src="https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/d8f982c8f97c6828001f5273e10565ff.png" 
                          alt="Bancolombia" 
                          className="w-6 h-6 sm:w-8 sm:h-8 object-contain"
                        />
                      </div>
                      <div className="text-left">
                        <p className="text-xs sm:text-sm text-blue-800 font-bold mb-1">{t('cart.transfer_pay')}</p>
                        <p className="text-[10px] sm:text-xs text-gray-600">
                          {t('cart.transfer_desc')}
                        </p>
                      </div>
                    </div>
                    
                    {/* Monto a pagar destacado */}
                    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-xl p-3 sm:p-4 mb-3 text-center">
                      <p className="text-[10px] sm:text-xs text-emerald-700 font-medium mb-1">
                        <i className="ri-money-dollar-circle-line mr-1"></i>
                        {t('cart.amount_to_transfer')}
                      </p>
                      <p className="text-xl sm:text-3xl font-bold text-emerald-700 tracking-tight">
                        {formatColombianPaymentAmount(total).primary}
                      </p>
                      {formatColombianPaymentAmount(total).showEquivalence ? (
                        <div className="mt-1.5 bg-white/60 rounded-lg px-3 py-1.5 inline-block">
                          <p className="text-[9px] sm:text-xs text-gray-600">
                            <i className="ri-exchange-line mr-1"></i>
                            {t('cart.equivalent_to')} <strong className="text-gray-800">{formatColombianPaymentAmount(total).secondary} {formatColombianPaymentAmount(total).secondaryLabel}</strong>
                          </p>
                        </div>
                      ) : (
                        <p className="text-[9px] sm:text-xs text-emerald-600 mt-1">
                          ≈ {formatColombianPaymentAmount(total).secondary} {formatColombianPaymentAmount(total).secondaryLabel}
                        </p>
                      )}
                    </div>

                    {/* Datos de la cuenta */}
                    <div className="bg-white rounded-xl p-3 sm:p-4 border border-blue-200 mb-3">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] sm:text-xs text-gray-500">{t('cart.bank')}</span>
                          <span className="text-[10px] sm:text-xs font-semibold text-gray-900">Bancolombia</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] sm:text-xs text-gray-500">{t('cart.account_type')}</span>
                          <span className="text-[10px] sm:text-xs font-semibold text-gray-900">{t('cart.savings_account')}</span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[10px] sm:text-xs text-gray-500">{t('cart.account_number')}</span>
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs sm:text-sm font-bold text-blue-700 tracking-wider">363-524841-69</span>
                            <button
                              onClick={copyAccountNumber}
                              className={`px-2 py-1 rounded-lg text-[9px] sm:text-[10px] font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center gap-0.5 ${
                                accountCopied
                                  ? 'bg-green-100 text-green-700 border border-green-200'
                                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200 border border-blue-200'
                              }`}
                            >
                              {accountCopied ? (
                                <><i className="ri-check-line"></i>{t('cart.copied')}</>
                              ) : (
                                <><i className="ri-file-copy-line"></i>{t('cart.copy')}</>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* QR Code */}
                    <div className="bg-white rounded-xl p-3 sm:p-4 border border-blue-200 text-center">
                      <p className="text-[10px] sm:text-xs text-gray-500 mb-2 font-medium">
                        <i className="ri-qr-code-line mr-1"></i>
                        {t('cart.scan_qr')}
                      </p>
                      <div className="w-36 h-36 sm:w-48 sm:h-48 mx-auto rounded-lg overflow-hidden border border-gray-200">
                        <img 
                          src="https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/347934ce9fc22086f1523c6a10eb4293.jpeg" 
                          alt="QR Bancolombia" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <p className="text-[9px] sm:text-[10px] text-gray-400 mt-2">{t('cart.digital_license_payment')}</p>
                    </div>

                    <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-[10px] sm:text-xs text-amber-800 font-medium text-center">
                        <i className="ri-camera-line mr-1"></i>
                        {t('cart.after_payment')}
                      </p>
                    </div>

                    {/* Botón WhatsApp para enviar comprobante */}
                    <a
                      href={`https://wa.me/79534940353?text=${encodeURIComponent(buildTransferWhatsAppMessage())}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-green-500 text-white text-xs sm:text-sm font-semibold px-4 py-2.5 rounded-xl hover:bg-green-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-whatsapp-line text-base sm:text-lg"></i>
                      {t('cart.send_receipt_whatsapp')}
                    </a>
                  </div>
                )}

                {paymentMethod === 'paypal' && (
                  <div className="bg-[#0070BA]/5 border border-[#0070BA]/20 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                    <div className="flex items-start gap-2 sm:gap-3 mb-2 sm:mb-3">
                      <i className="ri-information-line text-[#0070BA] text-sm sm:text-lg mt-0.5"></i>
                      <div className="text-left">
                        <p className="text-xs sm:text-sm text-[#0070BA] font-medium mb-1">{t('cart.paypal_pay')}</p>
                        <p className="text-[10px] sm:text-xs text-[#0070BA] mt-0.5 sm:mt-1">
                          {t('cart.paypal_desc')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Botón de enviar */}
                <button
                  type="submit"
                  disabled={!paymentMethod || !customerName.trim() || !customerEmail.trim() || !customerPhone.trim() || isSubmitting || Object.keys(errors).some(key => errors[key])}
                  className={`w-full py-3 sm:py-4 rounded-xl font-semibold text-sm sm:text-lg transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5 sm:gap-2 ${
                    !paymentMethod || !customerName.trim() || !customerEmail.trim() || !customerPhone.trim() || isSubmitting || Object.keys(errors).some(key => errors[key])
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : paymentMethod === 'whatsapp'
                        ? 'bg-green-500 text-white hover:bg-green-600 shadow-lg shadow-green-500/25'
                        : paymentMethod === 'nequi'
                          ? 'bg-[#E6007E] text-white hover:bg-[#C9006B] shadow-lg shadow-[#E6007E]/25'
                          : paymentMethod === 'efecty'
                            ? 'bg-[#FFD100] text-gray-900 hover:bg-[#E6BC00] shadow-lg shadow-[#FFD100]/25'
                            : paymentMethod === 'daviplata'
                              ? 'bg-[#ED1C24] text-white hover:bg-[#D41920] shadow-lg shadow-[#ED1C24]/25'
                              : paymentMethod === 'mercadopago'
                                ? 'bg-sky-500 text-white hover:bg-sky-600 shadow-lg shadow-sky-500/25'
                                : paymentMethod === 'transferencia'
                                  ? 'bg-[#0078D4] text-white hover:bg-[#006abc] shadow-lg shadow-[#0078D4]/25'
                                  : paymentMethod === 'paypal'
                                    ? 'bg-[#0070BA] text-white hover:bg-[#005ea6] shadow-lg shadow-[#0070BA]/25'
                                    : 'bg-gradient-to-r from-[#0078D4] to-[#00a2ed] text-white hover:from-[#006abc] hover:to-[#0090d4] shadow-lg shadow-blue-500/25'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      {t('cart.sending')}
                    </>
                  ) : paymentMethod === 'whatsapp' ? (
                    <>
                      <i className="ri-whatsapp-line text-lg sm:text-xl"></i>
                      <span className="hidden sm:inline">{t('cart.send_whatsapp')}</span>
                      <span className="sm:hidden">{t('cart.send_whatsapp_short')}</span>
                    </>
                  ) : paymentMethod === 'nequi' ? (
                    <>
                      <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center flex-shrink-0">
                        <img 
                          src="https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/9bbb0ea2fd6a8b625dfa0d1af5ad1fa4.png" 
                          alt="Nequi" 
                          className="w-5 h-5 object-contain"
                        />
                      </div>
                      {t('cart.confirm_nequi')}
                    </>
                  ) : paymentMethod === 'efecty' ? (
                    <>
                      <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center flex-shrink-0">
                        <img 
                          src="https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/056c5be3003cf61f3b6a652d0e578a82.png" 
                          alt="Efecty" 
                          className="w-5 h-5 object-contain"
                        />
                      </div>
                      {t('cart.confirm_efecty')}
                    </>
                  ) : paymentMethod === 'daviplata' ? (
                    <>
                      <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center flex-shrink-0">
                        <img 
                          src="https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/4738bf97cbdee510c76b19003d71dd2e.png" 
                          alt="Daviplata" 
                          className="w-5 h-5 object-contain"
                        />
                      </div>
                      {t('cart.confirm_daviplata')}
                    </>
                  ) : paymentMethod === 'mercadopago' ? (
                    <>
                      <i className="ri-secure-payment-line text-lg sm:text-xl"></i>
                      {t('cart.pay_mercadopago')}
                    </>
                  ) : paymentMethod === 'transferencia' ? (
                    <>
                      <i className="ri-bank-line text-lg sm:text-xl"></i>
                      {t('cart.confirm_order')}
                    </>
                  ) : paymentMethod === 'paypal' ? (
                    <>
                      <i className="ri-paypal-line text-lg sm:text-xl"></i>
                      {t('cart.pay_paypal')}
                    </>
                  ) : (
                    t('cart.select_payment')
                  )}
                </button>

                <p className="text-[9px] sm:text-xs text-gray-400 text-center">
                  <i className="ri-lock-line mr-1"></i>
                  {t('cart.info_protected')}
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
